import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ShieldCheck, Lock, FileText, Mail, ArrowRight, Loader2, CheckCircle2,
  BadgeCheck, ShieldAlert, ChevronDown, ChevronRight, ExternalLink, Cpu, Database, Server,
  Menu, X, Search, Copy, Clock, Send,
  HelpCircle, Layers, Timer, ClipboardCheck, ServerCog, Sparkles, ShieldQuestion, Activity,
} from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { accentTokens } from '../utils/contrast';
import { NovaMark } from './NovaLogo';
import { trackEvent } from '../utils/trustCenterAnalytics';
import { TCBadge, Callout, EmptyState as TCEmptyState } from './trust-center/primitives';
import { SearchOverlay, SearchTrigger } from './search/SearchOverlay';

const NOVA_URL = 'https://teamnova.ai';

/**
 * Branded "Trust Center Unavailable" page.
 *
 * Scope: rendered ONLY inside the public Trust Center flow when the requested
 * slug does not resolve to a viewable Trust Center (unpublished, disabled,
 * invalid slug, access restricted, maintenance, etc.). Not used by any
 * global error boundary, admin page, or workspace/auth route.
 *
 * Future-proofing: layout is state-agnostic. Pass a different {title,
 * message, tone} to reuse this shell for maintenance, access-restricted,
 * disabled, invalid-URL, etc. — only the copy changes.
 */
const TrustCenterUnavailable = ({
  title = 'Trust Center Unavailable',
  message = 'This Trust Center may have been unpublished, disabled, or the link may be incorrect.',
  showAboutNova = true,
  primaryAction, // optional { label, onClick | href }
  contact, // optional { label, href } — only rendered if provided
}) => (
  <div
    className="relative min-h-screen overflow-hidden bg-gradient-to-b from-[#F6F9FF] via-white to-[#F1F5F9] text-[#0A0A0A]"
    data-testid="public-trust-unavailable"
  >
    {/* Ambient decorative layer — soft radial gradients + faint dot grid.
        Purely aesthetic depth. Kept low-opacity to preserve elegance. */}
    <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
      <div className="absolute -top-40 -right-32 w-[560px] h-[560px] rounded-full blur-3xl opacity-45"
        style={{ background: 'radial-gradient(closest-side, rgba(15,98,254,0.30), rgba(15,98,254,0) 70%)' }} />
      <div className="absolute -bottom-56 -left-40 w-[600px] h-[600px] rounded-full blur-3xl opacity-30"
        style={{ background: 'radial-gradient(closest-side, rgba(148,163,184,0.35), rgba(148,163,184,0) 70%)' }} />
      <div className="absolute inset-0 opacity-[0.28]"
        style={{
          backgroundImage: 'radial-gradient(rgba(15,23,42,0.08) 1px, transparent 1px)',
          backgroundSize: '22px 22px',
          maskImage: 'radial-gradient(ellipse at 50% 30%, black 40%, transparent 75%)',
          WebkitMaskImage: 'radial-gradient(ellipse at 50% 30%, black 40%, transparent 75%)',
        }} />
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#CBD5E1] to-transparent" />
    </div>

    {/* Header — Nova brand slightly more intentional but still subtle */}
    <header className="relative z-10 px-6 sm:px-10 pt-6 flex items-center justify-between">
      <a href={NOVA_URL} target="_blank" rel="noopener noreferrer"
        className="inline-flex items-center gap-2.5 group"
        data-testid="tc-unavail-brand">
        <NovaMark size={30} />
        <div className="leading-tight">
          <div className="font-semibold tracking-tight text-[15px] text-[#0A0A0A]">Nova</div>
          <div className="text-[10px] uppercase tracking-[0.16em] font-semibold text-[#64748B]">Trust Platform</div>
        </div>
      </a>
      <a href={NOVA_URL} target="_blank" rel="noopener noreferrer"
        className="hidden sm:inline-flex items-center gap-1 text-xs font-medium text-[#475569] hover:text-[#0F62FE] transition-colors"
        data-testid="tc-unavail-nova-link">
        Visit Nova <ExternalLink className="w-3.5 h-3.5" />
      </a>
    </header>

    {/* Hero */}
    <main className="relative z-10 px-6 sm:px-10 pt-14 sm:pt-20 pb-20 max-w-6xl mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-7 space-y-5">
          <span
            className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#0F62FE] bg-[#EAF2FF] border border-[#CFE0FF] rounded-full px-2.5 py-1"
            data-testid="tc-unavail-eyebrow"
          >
            <ShieldQuestion className="w-3.5 h-3.5" /> Trust Center
          </span>
          {/* Refined heading — reduced dominance, tighter tracking, still clearly primary */}
          <h1
            className="font-semibold tracking-tight text-[28px] sm:text-4xl lg:text-[44px] leading-[1.1] text-[#0A0A0A] max-w-xl"
            data-testid="tc-unavail-title"
          >
            {title}
          </h1>
          <p
            className="text-[15px] sm:text-base text-[#475569] max-w-lg leading-relaxed"
            data-testid="tc-unavail-message"
          >
            {message}
          </p>
          {/* Helpful guidance — sits between the primary message and CTAs */}
          <div
            className="flex items-start gap-2.5 max-w-lg text-[13px] leading-relaxed text-[#334155] bg-white/60 border border-[#E2E8F0] rounded-xl px-3.5 py-3 backdrop-blur-sm"
            data-testid="tc-unavail-guidance"
          >
            <HelpCircle className="w-4 h-4 text-[#0F62FE] shrink-0 mt-0.5" />
            <span>
              If you expected to access this organization&rsquo;s Trust
              Center, please contact their security or compliance team.
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-3 pt-1" data-testid="tc-unavail-actions">
            {primaryAction ? (
              primaryAction.href ? (
                <a href={primaryAction.href}
                  className="inline-flex items-center gap-2 h-11 px-5 rounded-full text-sm font-semibold bg-[#0F62FE] text-white hover:bg-[#0A4FCC] transition-colors shadow-sm"
                  data-testid="tc-unavail-primary-btn">
                  {primaryAction.label} <ArrowRight className="w-4 h-4" />
                </a>
              ) : (
                <button type="button" onClick={primaryAction.onClick}
                  className="inline-flex items-center gap-2 h-11 px-5 rounded-full text-sm font-semibold bg-[#0F62FE] text-white hover:bg-[#0A4FCC] transition-colors shadow-sm"
                  data-testid="tc-unavail-primary-btn">
                  {primaryAction.label} <ArrowRight className="w-4 h-4" />
                </button>
              )
            ) : (
              <a href={NOVA_URL} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 h-11 px-5 rounded-full text-sm font-semibold bg-[#0F62FE] text-white hover:bg-[#0A4FCC] transition-colors shadow-sm"
              data-testid="tc-unavail-primary-btn">
                Learn about the Nova Platform <ArrowRight className="w-4 h-4" />
              </a>
            )}
            {contact && contact.href && (
              <a href={contact.href}
                className="inline-flex items-center gap-2 h-11 px-5 rounded-full text-sm font-semibold text-[#0A0A0A] bg-white border border-[#D7E0F2] hover:border-[#0F62FE] hover:text-[#0F62FE] transition-colors"
                data-testid="tc-unavail-contact-btn">
                <Mail className="w-4 h-4" /> {contact.label || 'Contact the organization'}
              </a>
            )}
          </div>
        </div>

        {/* Illustration — miniature Trust Center preview.
            Abstract, non-interactive; communicates "Trust Center" at a glance
            through header + tabs, framework tiles, policy rows and trust
            metrics — not a real UI screenshot. */}
        <div className="lg:col-span-5" aria-hidden data-testid="tc-unavail-illustration">
          <div className="relative mx-auto w-full max-w-md">
            {/* Layered depth: outer blue glow, softer inner glow, and a
                translucent secondary card offset behind the main mock for a
                premium sense of depth. Purely decorative — no animation. */}
            <div className="absolute -inset-10 rounded-[48px] blur-3xl opacity-70"
              style={{ background: 'radial-gradient(closest-side, rgba(15,98,254,0.22), rgba(15,98,254,0) 70%)' }} />
            <div className="absolute -inset-4 rounded-[32px] blur-xl opacity-70"
              style={{ background: 'radial-gradient(closest-side, rgba(76,141,255,0.18), rgba(76,141,255,0) 75%)' }} />
            <div className="absolute inset-x-4 inset-y-6 rounded-[28px] bg-white/70 border border-[#E2E8F0]/80 shadow-[0_20px_60px_-30px_rgba(15,23,42,0.25)] translate-x-3 translate-y-3" />

            {/* Floating trust chip — sits just outside the card for depth */}
            <div className="absolute -top-3 -left-3 z-10 inline-flex items-center gap-1.5 text-[10px] font-semibold text-[#0F62FE] bg-white border border-[#CFE0FF] rounded-full px-2.5 py-1 shadow-[0_10px_24px_-12px_rgba(15,98,254,0.45)]"
              data-testid="tc-unavail-chip-verified">
              <BadgeCheck className="w-3 h-3" /> Verified
            </div>
            <div className="absolute -bottom-3 -right-3 z-10 inline-flex items-center gap-1.5 text-[10px] font-semibold text-[#0F62FE] bg-white border border-[#CFE0FF] rounded-full px-2.5 py-1 shadow-[0_10px_24px_-12px_rgba(15,98,254,0.45)]"
              data-testid="tc-unavail-chip-encrypted">
              <Lock className="w-3 h-3" /> Encrypted
            </div>

            <div className="relative rounded-3xl bg-white/95 border border-[#E2E8F0] shadow-[0_36px_90px_-32px_rgba(15,23,42,0.35),0_10px_30px_-16px_rgba(15,98,254,0.18)] backdrop-blur-sm p-4 sm:p-5 space-y-4">
              {/* Mock header bar */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center text-white shadow-[0_6px_16px_-6px_rgba(15,98,254,0.55)]"
                    style={{ background: 'linear-gradient(135deg, #0F62FE 0%, #4C8DFF 100%)' }}>
                    <ShieldCheck className="w-4 h-4" strokeWidth={2} />
                  </div>
                  <div className="leading-tight">
                    <div className="h-2 w-20 rounded-full bg-[#0F172A]" />
                    <div className="mt-1 h-1.5 w-14 rounded-full bg-[#CBD5E1]" />
                  </div>
                </div>
                <span className="inline-flex items-center gap-1 text-[9px] font-semibold uppercase tracking-[0.14em] text-[#0F62FE] bg-[#EAF2FF] border border-[#CFE0FF] rounded-full px-1.5 py-0.5">
                  <BadgeCheck className="w-2.5 h-2.5" /> Trust
                </span>
              </div>
              {/* Mock tab strip */}
              <div className="flex items-center gap-3 border-b border-[#E2E8F0] pb-2">
                {['Overview', 'Frameworks', 'Policies', 'Controls'].map((t, i) => (
                  <div key={t} className={`h-1.5 rounded-full ${i === 0 ? 'w-14 bg-[#0F62FE]' : 'w-10 bg-[#E2E8F0]'}`} />
                ))}
              </div>
              {/* Compliance framework tiles */}
              <div className="grid grid-cols-3 gap-2">
                {[
                  { label: 'SOC 2', Icon: BadgeCheck },
                  { label: 'ISO 27001', Icon: Layers },
                  { label: 'GDPR', Icon: ShieldCheck },
                ].map(({ label, Icon }) => (
                  <div key={label} className="flex flex-col items-start gap-1 rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] px-2.5 py-2">
                    <Icon className="w-3.5 h-3.5 text-[#0F62FE]" />
                    <span className="text-[10px] font-semibold text-[#0A0A0A]">{label}</span>
                    <div className="h-1 w-full rounded-full bg-[#E2E8F0] overflow-hidden">
                      <div className="h-full rounded-full bg-[#0F62FE]" style={{ width: `${label === 'GDPR' ? 62 : label === 'ISO 27001' ? 84 : 96}%` }} />
                    </div>
                  </div>
                ))}
              </div>
              {/* Trust metrics row */}
              <div className="grid grid-cols-3 gap-2">
                {[
                  { k: '128', v: 'Controls' },
                  { k: '24', v: 'Policies' },
                  { k: '99.9%', v: 'Uptime' },
                ].map(({ k, v }) => (
                  <div key={v} className="rounded-xl border border-[#E2E8F0] bg-white px-2.5 py-2">
                    <div className="text-[13px] font-semibold text-[#0A0A0A] leading-none">{k}</div>
                    <div className="mt-1 text-[9px] uppercase tracking-[0.14em] text-[#64748B] font-semibold">{v}</div>
                  </div>
                ))}
              </div>
              {/* Documentation / policy rows */}
              <div className="space-y-1.5">
                {[
                  { label: 'Information Security Policy', tag: 'v3.1' },
                  { label: 'Data Processing Agreement', tag: 'v2.0' },
                  { label: 'Access Control Policy', tag: 'v1.4' },
                ].map(({ label, tag }) => (
                  <div key={label} className="flex items-center gap-2 rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] px-2.5 py-2">
                    <FileText className="w-3.5 h-3.5 text-[#0F62FE] shrink-0" />
                    <span className="text-[11px] font-medium text-[#0A0A0A] truncate">{label}</span>
                    <span className="ml-auto text-[9px] font-semibold text-[#64748B] bg-white border border-[#E2E8F0] rounded-full px-1.5 py-0.5">{tag}</span>
                    <Lock className="w-3 h-3 text-[#94A3B8]" />
                  </div>
                ))}
              </div>
              {/* Continuous monitoring bar */}
              <div className="flex items-center gap-2 text-[10px] font-semibold text-[#0F62FE] bg-[#EAF2FF] border border-[#CFE0FF] rounded-lg px-2.5 py-1.5">
                <Sparkles className="w-3 h-3" /> Continuously monitored
                <span className="ml-auto inline-flex items-center gap-1 text-[#16A34A]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#16A34A]" /> Live
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* About Nova — 2-column: brand + intro on the left, description +
          feature highlights on the right. Improves scannability without
          adding content. Vertical spacing tightened (mt-20 → mt-14) so the
          hero and this section feel cohesive rather than disconnected. */}
      {showAboutNova && (
        <section
          className="mt-14 rounded-3xl border border-[#E2E8F0] bg-white/85 backdrop-blur-sm p-8 sm:p-10 shadow-[0_12px_40px_-24px_rgba(15,23,42,0.18)]"
          data-testid="tc-unavail-about-nova"
        >
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 items-start">
            <div className="md:col-span-5">
              <div className="inline-flex items-center gap-2 mb-3">
                <NovaMark size={26} />
                <span className="font-semibold text-[16px] text-[#0A0A0A]">Nova</span>
              </div>
              <p className="text-[11px] uppercase tracking-[0.16em] text-[#94A3B8] font-semibold mb-1.5">
                About Nova
              </p>
              <h2 className="text-[20px] sm:text-[22px] font-semibold tracking-tight text-[#0A0A0A] leading-snug">
                AI-native GRC &amp; Trust Platform
              </h2>
              <p className="mt-3 text-[13px] leading-relaxed text-[#64748B]">
                Automate compliance, monitor posture continuously, and
                securely share proof with customers.
              </p>
            </div>
            <div className="md:col-span-7">
              <p className="text-[13px] leading-relaxed text-[#334155] mb-4">
                Nova is an AI-native governance, risk, compliance, and trust
                platform that helps organizations automate compliance,
                continuously monitor security posture, manage policies, and
                securely share trust information through customizable Trust
                Centers.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {[
                  { icon: ClipboardCheck, title: 'Automated Compliance', hint: 'Continuous framework mapping' },
                  { icon: Activity, title: 'Continuous Monitoring', hint: 'Live posture across systems' },
                  { icon: FileText, title: 'Policy Management', hint: 'Versioning, approvals, publishing' },
                  { icon: ShieldCheck, title: 'Customer Trust Centers', hint: 'Public, branded, always up-to-date' },
                ].map(({ icon: Ic, title: t, hint }) => (
                  <div key={t} className="flex items-start gap-2.5 rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] px-3 py-2.5">
                    <span className="w-7 h-7 rounded-lg bg-[#EAF2FF] text-[#0F62FE] inline-flex items-center justify-center shrink-0">
                      <Ic className="w-3.5 h-3.5" />
                    </span>
                    <div className="min-w-0">
                      <div className="text-[13px] font-semibold text-[#0A0A0A] leading-tight">{t}</div>
                      <div className="text-[11px] text-[#64748B] leading-snug mt-0.5">{hint}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="pt-4">
                <a href={NOVA_URL} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0F62FE] hover:text-[#0A4FCC]"
                  data-testid="tc-unavail-learn-link">
                  Explore the Nova platform <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Footer — subtle trust attribution */}
      <p className="mt-8 text-center text-xs text-[#94A3B8]" data-testid="tc-unavail-footer">
        Powered by{' '}
        <a href={NOVA_URL} target="_blank" rel="noopener noreferrer" className="font-semibold text-[#475569] hover:text-[#0F62FE]">
          Nova Trust Platform
        </a>
      </p>
    </main>
  </div>
);

const SECTIONS = [
  { key: 'overview', slug: 'overview', label: 'Overview' },
  { key: 'security', slug: 'security', label: 'Security' },
  { key: 'compliance', slug: 'compliance', label: 'Compliance' },
  { key: 'resources', slug: 'resources', label: 'Resources' },
  { key: 'subprocessors', slug: 'subprocessors', label: 'Subprocessors' },
  { key: 'faq', slug: 'faq', label: 'FAQ' },
  { key: 'contact', slug: 'contact', label: 'Contact' },
];
const SLUG_TO_KEY = Object.fromEntries(SECTIONS.map((s) => [s.slug, s.key]));
const KEY_TO_SLUG = Object.fromEntries(SECTIONS.map((s) => [s.key, s.slug]));
const SECTION_LABEL = Object.fromEntries(SECTIONS.map((s) => [s.key, s.label]));

// --- Framework deep-linking (Compliance Snapshot -> filtered Controls) ---
// Maps a framework display name (from config or a control) to a stable URL token,
// and back to a human label. Robust to naming variations (e.g. "SOC 2" / "SOC2").
const frameworkToken = (name) => {
  const n = (name || '').toString().toLowerCase();
  if (!n) return '';
  if (n.includes('soc')) return 'soc2';
  if (n.includes('iso')) return 'iso27001';
  if (n.includes('hipaa')) return 'hipaa';
  if (n.includes('pci')) return 'pcidss';
  if (n.includes('gdpr')) return 'gdpr';
  if (n.includes(' ai') || n.includes('ai ') || n === 'ai' || n.includes('privacy')) return 'ai';
  return n.replace(/[^a-z0-9]+/g, ''); // safe fallback token
};
const FW_TOKEN_LABEL = {
  soc2: 'SOC 2', iso27001: 'ISO 27001', hipaa: 'HIPAA',
  gdpr: 'GDPR', pcidss: 'PCI DSS', ai: 'AI & Privacy',
};
const frameworkLabelFromToken = (token) => FW_TOKEN_LABEL[token] || titleize(token);

const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return ''; }
};

const titleize = (s) => (s || '').toString().replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()).trim();


const copyText = async (text) => {
  try { await navigator.clipboard.writeText(text); toast.success('Link copied'); }
  catch (e) {
    try {
      const ta = document.createElement('textarea'); ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.focus(); ta.select(); document.execCommand('copy'); document.body.removeChild(ta);
      toast.success('Link copied');
    } catch (_) { toast.error('Copy failed'); }
  }
};

// Canonical 4-tier access model: Public / Request Access / Available under NDA
// / Internal Only (Internal never reaches the public payload). The legacy
// "restricted" tier has been retired and maps to Request Access.
const ACCESS_META = {
  public: { label: 'Public', locked: false },
  request: { label: 'Request Access', locked: true },
  nda: { label: 'Available under NDA', locked: true },
};
const accessMeta = (a) => (a === 'restricted' ? ACCESS_META.request : (ACCESS_META[a] || ACCESS_META.request));

/* -----------------------------------------------------------------------------
 * Sanitiser — hide internal/test artefacts from the public Trust Center.
 * Never modifies persisted data; only affects what the public renderer shows.
 * Matches conservative allow-listed patterns so real content is never dropped
 * (e.g. a resource named "SUGGESTION FOR VENDORS" would NOT be filtered — we
 * anchor on TOKEN_ prefixes with an underscore, dev fixture phrases, etc.).
 * --------------------------------------------------------------------------- */
const INTERNAL_PATTERNS = [
  /^TC_CAT_/i,           // category placeholders
  /^BULK_CAT_/i,
  /^SUG_/i,              // suggestion tokens
  /^ARCHIVE_ME_/i,
  /^PATCH[-_]TEST/i,
  /^TEST[-_]/i,          // TEST_UI_VER_*, TEST_VERS_*, TEST-...
  /regression\s*fixture/i,
  /^policy\s*testing($|[\s_-])/i,
  /development\s*fixture/i,
  /internal[- ]?hash/i,
];
const looksInternal = (raw) => {
  const s = (raw || '').toString().trim();
  if (!s) return false;
  return INTERNAL_PATTERNS.some((rx) => rx.test(s));
};
const isInternalDoc = (d) => looksInternal(d?.title) || looksInternal(d?.doc_type) || looksInternal(d?.category);
const isInternalControl = (c) => looksInternal(c?.title) || looksInternal(c?.category) || looksInternal(c?.category_id);
const isInternalUpdate = (u) => looksInternal(u?.title) || looksInternal(u?.description);
const isInternalFaq = (f) => looksInternal(f?.question) || looksInternal(f?.answer);
const isInternalSubprocessor = (s) => looksInternal(s?.name) || looksInternal(s?.purpose);

// Public metrics whitelist — per user spec: hide "Resources Available" and
// "Integrations Connected" (they do not build customer trust). Keep frameworks,
// policies, controls, audit status, last-review, and other trust signals.
const HIDDEN_KPI_KEYS = new Set(['resources_available', 'integrations_connected', 'resources', 'integrations']);

// Canonical CTA vocabulary used across every view.
const CTA = {
  view: 'View',
  download: 'Download',
  open: 'Open',
  request: 'Request Access',
  requestNda: 'Request under NDA',
  requestDocs: 'Request Documentation',
  contact: 'Contact Security',
};
// Nova-brokered public delivery URL for an uploaded, Public resource.
const publicFileUrl = (slug, id, download) =>
  `/api/public/trust/${encodeURIComponent(slug)}/documents/${id}/file${download ? '?download=1' : ''}`;


/* -----------------------------------------------------------------------------
 * DocActions — the single shared resource-action control used by EVERY surface
 * (Overview, Resources, Security, Compliance, Search, Preview). Guarantees one
 * consistent behaviour contract:
 *   • Public          → frictionless Open (+ Download when Nova-hosted). No
 *                        lock, no request, no popup.
 *   • Available under NDA → "Request under NDA".
 *   • Request Access  → "Request Access".
 * In admin Preview mode, Open/Download are inert (files serve only on the
 * published Trust Center) so the layout stays identical without 404s.
 * --------------------------------------------------------------------------- */
const DocActions = ({ ctx, d, variant = 'button', testid }) => {
  const { accent, aText, onAcc, openRequest, slug, mode } = ctx;
  const acc = d.access || 'request';
  const btnCls = 'px-3 py-1.5 rounded-full text-[11px] uppercase tracking-wider font-semibold hover:opacity-90 transition-opacity inline-flex items-center gap-1 whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F62FE]/40';
  const outlineCls = 'px-3 py-1.5 rounded-full text-[11px] uppercase tracking-wider font-semibold border transition-colors hover:bg-[#F8FAFC] inline-flex items-center gap-1 whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F62FE]/30';
  const linkCls = 'text-xs font-semibold hover:underline whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F62FE]/30 rounded inline-flex items-center gap-1';

  if (acc === 'public') {
    const hasFile = !!d.has_file;
    const openHref = hasFile ? publicFileUrl(slug, d.id, false) : (d.external_link || '');
    const dlHref = hasFile ? publicFileUrl(slug, d.id, true) : '';
    if (!openHref) {
      return (
        <button type="button" onClick={() => openRequest([d])} data-testid={testid}
          className={variant === 'link' ? linkCls : btnCls}
          style={variant === 'link' ? { color: aText } : { backgroundColor: accent, color: onAcc }}>{CTA.request}</button>
      );
    }
    const inert = mode !== 'public';
    const guard = (e) => { if (inert) { e.preventDefault(); toast.info('Available on the published Trust Center.'); } };
    if (variant === 'link') {
      return (
        <span className="inline-flex items-center gap-3">
          <a href={openHref} target="_blank" rel="noreferrer" onClick={guard} className={linkCls} style={{ color: aText }} data-testid={testid ? `${testid}-open` : undefined}>{CTA.open} <ExternalLink className="w-3 h-3" /></a>
          {hasFile && <a href={dlHref} onClick={guard} className={linkCls} style={{ color: aText }} data-testid={testid ? `${testid}-download` : undefined}>{CTA.download}</a>}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-2">
        <a href={openHref} target="_blank" rel="noreferrer" onClick={guard} className={btnCls} style={{ backgroundColor: accent, color: onAcc }} data-testid={testid ? `${testid}-open` : testid}>{CTA.open}{!hasFile && <ExternalLink className="w-3 h-3" />}</a>
        {hasFile && <a href={dlHref} onClick={guard} className={outlineCls} style={{ borderColor: '#CBD5E1', color: aText }} data-testid={testid ? `${testid}-download` : undefined}>{CTA.download}</a>}
      </span>
    );
  }

  const label = acc === 'nda' ? CTA.requestNda : CTA.request;
  if (variant === 'link') {
    return <button type="button" onClick={() => openRequest([d])} className={linkCls} style={{ color: aText }} data-testid={testid}>{label}</button>;
  }
  return <button type="button" onClick={() => openRequest([d])} data-testid={testid} className={btnCls} style={{ backgroundColor: accent, color: onAcc }}>{label}</button>;
};

const docMatches = (d, keywords, sectionKey) => {
  if (sectionKey && d.related_section === sectionKey) return true;
  const hay = `${d.title} ${d.doc_type} ${d.related_framework}`.toLowerCase();
  return keywords.some((k) => k && hay.includes(k.toLowerCase()));
};

const safeUrl = (u) => { const s = (u || '').trim(); return /^https?:\/\//i.test(s) ? s : ''; };

// Tiny debounced search-tracking hook — only fires in public mode with a slug.
const useSearchTracking = (mode, slug, query, targetName) => {
  useEffect(() => {
    if (mode !== 'public' || !slug) return undefined;
    const q = (query || '').trim();
    if (!q || q.length < 2) return undefined;
    const t = setTimeout(() => {
      trackEvent(slug, 'search', { search_query: q, target_name: targetName });
    }, 900);
    return () => clearTimeout(t);
  }, [mode, slug, query, targetName]);
};

// JS port of backend public_view(): turns the admin draft (cfg + posture) into the
// EXACT same sanitized shape the public page consumes — so the Live Preview renders
// through the same component tree. Keep in sync with services/trust_center.py.
export const buildPublicData = (cfg, posture, controls = []) => {
  if (!cfg) return null;
  const sections = cfg.sections || {};
  const showMetrics = !!cfg.show_control_metrics;
  const accent = (cfg.theme && cfg.theme.accent_color) || '#0F62FE';

  const enabledCards = (cards) => (cards || [])
    .filter((c) => c.enabled !== false && ((c.title || '').trim() || (c.description || '').trim()))
    .map((c) => { const item = { id: c.id, title: (c.title || '').trim(), description: (c.description || '').trim() }; const t = (c.type || '').trim().toLowerCase(); if (t === 'administrative' || t === 'technical') item.type = t; return item; });

  const postureByName = Object.fromEntries((posture?.frameworks || []).map((f) => [f.name, f]));
  const frameworks = (cfg.compliance_frameworks || [])
    .filter((f) => f.enabled !== false && f.published !== false && (f.name || '').trim())
    .map((f) => {
      const item = { name: (f.name || '').trim(), status: (f.status || '').trim(), last_reviewed: (f.last_reviewed || '').trim(), audit_status: (f.audit_status || '').trim() };
      if (showMetrics) { const p = postureByName[item.name]; if (p) { item.satisfied_percent = p.satisfied_percent; item.total_controls = p.total_controls; item.satisfied_controls = p.satisfied_controls; } }
      return item;
    });
  // Admin-featured Compliance frameworks for the Overview (ordered).
  const _featFwNames = (cfg.compliance_frameworks || [])
    .filter((f) => f.enabled !== false && f.published !== false && (f.name || '').trim() && f.featured_on_overview)
    .sort((a, b) => (parseInt(a.overview_order || 0, 10) - parseInt(b.overview_order || 0, 10)) || (a.name || '').localeCompare(b.name || ''))
    .map((f) => (f.name || '').trim());
  const overview_featured_frameworks = _featFwNames
    .map((n) => frameworks.find((fw) => fw.name === n))
    .filter(Boolean);

  let metrics = null;
  if (showMetrics && posture?.has_data) metrics = { total_controls: posture.total_controls, satisfied_controls: posture.satisfied_controls, satisfied_percent: posture.satisfied_percent };

  const VIS_TO_ACCESS = { public: 'public', gated: 'request', nda: 'nda', hidden: 'hidden' };
  const resolveAccess = (d) => { const a = (d.access || '').toLowerCase(); if (a === 'restricted') return 'request'; if (['public', 'request', 'nda', 'hidden'].includes(a)) return a; return VIS_TO_ACCESS[(d.visibility || 'gated').toLowerCase()] || 'request'; };
  const docStatus = (d) => { const s = (d.status || '').toLowerCase(); return ['draft', 'published', 'archived'].includes(s) ? s : 'published'; };
  const hasFile = (d) => !!(d.file && d.file.id && d.file.storage_path);
  const hasDeliverable = (d) => hasFile(d) || !!safeUrl(d.external_link);
  // Policy-linked listings only surface when explicitly included AND their
  // source workspace policy is still published (mirrors backend gate).
  const policyVisible = (d) => !d.policy_id || ((d.included_in_trust_center !== false) && !d.policy_missing && ((d.policy_status || 'published').toLowerCase() === 'published'));
  const documents = (cfg.documents || [])
    .filter((d) => resolveAccess(d) !== 'hidden' && docStatus(d) === 'published' && policyVisible(d) && (d.title || '').trim() && (d.policy_id || d.published !== false))
    .map((d) => {
      let access = resolveAccess(d);
      // Public deliverability guard (mirror backend): a non-policy Public
      // resource with no file and no valid link renders as Request Access.
      if (access === 'public' && !d.policy_id && !hasDeliverable(d)) access = 'request';
      const relFws = (d.related_frameworks || []).filter(Boolean);
      const relSingle = (d.related_framework || '').trim();
      const frameworks = relSingle && !relFws.includes(relSingle) ? [relSingle, ...relFws] : relFws;
      if (access === 'nda') {
        // Mirror backend: NDA-listed communicates existence only.
        return { id: d.id, title: (d.title || '').trim(), doc_type: (d.doc_type || '').trim(), description: '', access: 'nda', category: (d.category || '').trim(), tags: [], external_link: '', has_file: false, related_framework: relSingle, related_frameworks: frameworks, version: 0, effective_date: '', next_review_date: '', related_section: (d.related_section || '').trim(), last_updated: '', policy_type: '', is_policy: !!d.policy_id };
      }
      return { id: d.id, title: (d.title || '').trim(), doc_type: (d.doc_type || '').trim(), description: (d.description || '').trim(), access, category: (d.category || '').trim(), tags: (d.tags || []).filter(Boolean), external_link: access === 'public' ? safeUrl(d.external_link) : '', has_file: access === 'public' && hasFile(d), file_name: access === 'public' && hasFile(d) ? (d.file.name || '') : '', related_framework: relSingle, related_frameworks: frameworks, version: d.policy_id ? (parseInt(d.policy_version, 10) || 1) : (parseInt(d.version, 10) || 1), effective_date: (d.effective_date || '').trim(), next_review_date: d.show_next_review ? (d.next_review_date || '').trim() : '', related_section: (d.related_section || '').trim(), last_updated: (d.last_updated || '').trim(), policy_type: d.policy_id ? (d.policy_type || '').trim() : '', is_policy: !!d.policy_id };
    });

  const recent_updates = (cfg.recent_updates || [])
    .filter((u) => !u.hidden && (u.title || '').trim())
    .map((u) => ({ id: u.id, title: (u.title || '').trim(), type: (u.type || '').trim(), date: (u.date || '').trim(), description: (u.description || '').trim() }))
    .sort((a, b) => (b.date || '').localeCompare(a.date || ''));

  const subprocessors = (cfg.subprocessors || [])
    .filter((s) => (s.vendor_name || '').trim() && s.published !== false)
    .map((s) => ({ id: s.id, vendor_name: (s.vendor_name || '').trim(), purpose: (s.purpose || '').trim(), data_processed: (s.data_processed || '').trim(), location: (s.location || '').trim(), privacy_url: safeUrl(s.privacy_url), status: (s.status || '').trim() }));

  const faqs = (cfg.faqs || [])
    .filter((f) => (f.question || '').trim() && (f.answer || '').trim() && f.published !== false)
    .map((f) => ({ id: f.id, question: (f.question || '').trim(), answer: (f.answer || '').trim() }));

  const aiGov = cfg.ai_governance || {};
  const ai_governance = { statement: (aiGov.statement || '').trim(), items: enabledCards(aiGov.items) };
  const cs = cfg.controls_settings || {};

  const disc = cfg.disclosure || {};
  const disclosure = { statement: (disc.statement || '').trim(), response_sla: (disc.response_sla || '').trim(), reporting_instructions: (disc.reporting_instructions || '').trim() };
  const footer = cfg.footer || {};

  // ---- Executive KPI summary + compliance snapshot (mirror backend public_view) ----
  const publishedControls = (controls || []).length;
  const documentsList = documents; // computed above
  const policiesAvailable = documentsList.filter((d) => d.category === 'Policy' || (d.doc_type || '').toLowerCase().includes('policy')).length;
  const integrations = (posture?.integrations || []).map((i) => (i || '').toString().trim()).filter(Boolean);
  const last_audit = posture?.last_audit || null;
  const kpis = [];
  if (frameworks.length > 0) kpis.push({ key: 'frameworks', label: 'Compliance Frameworks', value: frameworks.length, sublabel: '' });
  if (publishedControls > 0) kpis.push({ key: 'controls', label: 'Requirements Mapped', value: publishedControls, sublabel: '' });
  if (policiesAvailable > 0) kpis.push({ key: 'policies', label: 'Policies Available', value: policiesAvailable, sublabel: '' });
  if (integrations.length > 0) kpis.push({ key: 'integrations', label: 'Integrations Connected', value: integrations.length, sublabel: integrations.slice(0, 4).join(', ') });
  if (subprocessors.length > 0) kpis.push({ key: 'subprocessors', label: 'Subprocessors', value: subprocessors.length, sublabel: '' });
  if (documentsList.length > 0) kpis.push({ key: 'resources', label: 'Resources Available', value: documentsList.length, sublabel: '' });
  if (last_audit && (last_audit.name || last_audit.status)) kpis.push({ key: 'last_audit', label: 'Last Audit', value: (last_audit.name || '—'), sublabel: (last_audit.status || '') });
  const compliance_snapshot = frameworks.map((fw) => {
    const row = { name: fw.name, status: fw.audit_status || fw.status || '', last_updated: fw.last_reviewed || '', coverage: null };
    if (showMetrics && fw.total_controls != null) row.coverage = { total_controls: fw.total_controls, satisfied_percent: fw.satisfied_percent };
    return row;
  });

  // ---- Featured resources per Overview category (mirrors backend) ----
  // Public parity: ONLY explicit featured_on_overview=true resources appear.
  // For empty categories with published resources, emit a safe fallback
  // (label + count + slug) so the public Overview renders a summary line
  // instead of silently promoting arbitrary content.
  const internalById = new Map((cfg.documents || []).map((d) => [d.id, d]));
  const featSort = (a, b) => {
    const ra = internalById.get(a.id) || {}; const rb = internalById.get(b.id) || {};
    const foa = parseInt(ra.featured_order || 0, 10); const fob = parseInt(rb.featured_order || 0, 10);
    if (foa !== fob) return foa - fob;
    const doa = parseInt(ra.display_order || 0, 10); const dob = parseInt(rb.display_order || 0, 10);
    if (doa !== dob) return doa - dob;
    return (rb.last_updated || '').localeCompare(ra.last_updated || '');
  };
  const featured_by_category = {};
  ['Compliance', 'Security', 'Privacy', 'AI Governance'].forEach((cat) => {
    const pool = documents.filter((d) => (d.category || '') === cat && !d.is_policy);
    const explicit = pool.filter((d) => (internalById.get(d.id) || {}).featured_on_overview);
    explicit.sort(featSort);
    featured_by_category[cat] = explicit.slice(0, 4);
  });

  // Phase 2: Security Profile preview — mirror backend _build_public_security_profile.
  // Only enabled sections with real content emit; cross-linked ids are resolved
  // against the ALREADY-sanitized pools so nothing that would not appear
  // elsewhere can leak into the preview.
  const secProfileSrc = cfg.security_profile || {};
  const docsById = new Map(documents.map((d) => [d.id, d]));
  const docByPolicyId = new Map(documents.filter((d) => d.policy_id || (internalById.get(d.id) || {}).policy_id).map((d) => {
    const raw = internalById.get(d.id) || {};
    return [raw.policy_id, d];
  }));
  const controlsPublicList = cs.show_status ? (controls || []) : (controls || []).map(({ status, ...c }) => c);
  const controlsById = new Map(controlsPublicList.map((c) => [c.id, c]));
  const faqsById = new Map(faqs.map((f) => [f.id, f]));
  const security_profile = {};
  const SECURITY_SECTION_KEYS = ['identity_access','infrastructure','encryption','application_api_security','logging_monitoring','vulnerability_management','secure_development','business_continuity','data_privacy','ai_governance'];
  const SECURITY_TITLES = { identity_access:'Identity & Access', infrastructure:'Infrastructure', encryption:'Encryption', application_api_security:'Application & API Security', logging_monitoring:'Logging & Monitoring', vulnerability_management:'Vulnerability Management', secure_development:'Secure Development', business_continuity:'Business Continuity', data_privacy:'Data Privacy', ai_governance:'AI Governance' };
  SECURITY_SECTION_KEYS.forEach((k) => {
    const s = secProfileSrc[k] || {};
    if (s.enabled === false) return;
    if (s.published === false) return;  // Private by Default
    const overview = (s.overview || '').trim();
    const bullets = (s.bullets || []).map((b) => (b || '').trim()).filter(Boolean).slice(0, 12);
    const related_policies = (s.policy_ids || []).map((pid) => docByPolicyId.get(pid)).filter(Boolean).map((d) => ({ id: d.id, title: d.title, access: d.access, external_link: d.external_link || '', policy_type: d.policy_type || '' }));
    const related_documents = (s.document_ids || []).map((id) => docsById.get(id)).filter((d) => d && !d.policy_id && !(internalById.get(d.id) || {}).policy_id).map((d) => ({ id: d.id, title: d.title, access: d.access, category: d.category || '', doc_type: d.doc_type || '', external_link: d.external_link || '' }));
    const related_controls = (s.control_ids || []).map((id) => controlsById.get(id)).filter(Boolean).map((c) => ({ id: c.id, title: c.title, category: c.category || '', framework: c.framework || '' }));
    const related_faqs = (s.faq_ids || []).map((id) => faqsById.get(id)).filter(Boolean).map((f) => ({ id: f.id, question: f.question }));
    const hasContent = overview || bullets.length || related_policies.length || related_documents.length || related_controls.length || related_faqs.length;
    if (!hasContent) return;
    security_profile[k] = { key: k, title: SECURITY_TITLES[k], overview, bullets, related_policies, related_documents, related_controls, related_faqs,
      // Phase 1 audit-requirements: pass program cards + gap statements
      // through the preview unchanged. In the admin Live Preview these
      // arrive already-tier-filtered by the backend at fetch time.
      program_card: s.program_card || null,
      gap_statements: s.gap_statements || [],
    };
  });

  const overview_featured_sections = Object.keys(security_profile)
    .filter((k) => (secProfileSrc[k] || {}).featured_on_overview)
    .sort((a, b) => (parseInt((secProfileSrc[a] || {}).overview_order || 0, 10) - parseInt((secProfileSrc[b] || {}).overview_order || 0, 10)) || a.localeCompare(b))
    .map((k) => ({ key: k, title: security_profile[k].title, overview: security_profile[k].overview || '', bullets: (security_profile[k].bullets || []).slice(0, 4) }));

  return {
    slug: cfg.slug, company_name: cfg.company_name, security_contact_email: cfg.security_contact_email,
    trust_statement: (cfg.trust_statement || '').trim(), hero: cfg.hero || {},
    theme: { accent_color: accent, logo_url: safeUrl(cfg.theme?.logo_url) },
    sections: Object.fromEntries(SECTIONS.map((s) => [s.key, !!sections[s.key]])),
    show_control_metrics: showMetrics, overview_text: (cfg.overview_text || '').trim(),
    trust_indicators: (cfg.trust_indicators || [])
      .filter((t) => t && t.enabled !== false && (t.label || '').trim())
      .slice(0, 8)
      .map((t) => ({ id: t.id, label: (t.label || '').trim(), target: SECTIONS.some((s) => s.key === t.target) ? t.target : '', target_query: (t.target_query || '').trim() })),
    kpis, compliance_snapshot, last_audit,
    frameworks, metrics,
    // After resource_migration_v2, legacy narrative arrays are empty by
    // design — the categorized Resources drive Overview featured lists.
    security_program: cfg.resource_migration_v2 ? [] : enabledCards(cfg.security_program),
    privacy_handling: cfg.resource_migration_v2 ? [] : enabledCards(cfg.privacy_handling),
    ai_governance: cfg.resource_migration_v2 ? { statement: ai_governance.statement, items: [] } : ai_governance,
    security_profile,
    featured_by_category,
    overview_featured_frameworks,
    overview_featured_sections,
    resource_migration_v2: !!cfg.resource_migration_v2,
    documents,
    controls: cs.show_status ? (controls || []) : (controls || []).map(({ status, ...c }) => c),
    controls_settings: { show_status: !!cs.show_status, show_controls: cs.show_controls === true },
    recent_updates, subprocessors, faqs, disclosure,
    footer: { privacy_url: safeUrl(footer.privacy_url), terms_url: safeUrl(footer.terms_url) },
    last_updated_at: cfg.last_updated_at, published_at: cfg.published_at,
  };
};

// Section navigation that works in BOTH public (router URLs) and preview (in-memory state).
const NavTo = ({ ctx, sectionKey, className, style, children, testid, onClick }) => {
  if (ctx.mode === 'preview') {
    return <button type="button" data-testid={testid} className={className} style={style} onClick={(e) => { if (onClick) onClick(e); ctx.navigate(sectionKey); }}>{children}</button>;
  }
  return <Link to={`/t/${ctx.slug}/${KEY_TO_SLUG[sectionKey]}`} data-testid={testid} className={className} style={style} onClick={onClick}>{children}</Link>;
};

const PublicTrustCenter = () => {
  const { slug, section: sectionSlug } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`/api/public/trust/${slug}`);
      setData(data);
      document.title = `${data.company_name} Trust Center`;
    } catch (e) { setNotFound(true); }
    finally { setLoading(false); }
  }, [slug]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { window.scrollTo(0, 0); }, [sectionSlug]);

  // ---- SEO: robots meta management (Feb 2026) ------------------------------
  // Safe default: emit `noindex, nofollow` from the moment a /t/* route mounts,
  // BEFORE the API responds. This protects the brief pre-fetch window and any
  // error / 404 states from being indexed. Once the config confirms the Trust
  // Center is published AND the workspace has search-indexing enabled, we
  // remove the tag so crawlers may index normally. Cleaned up on unmount so
  // it never leaks into other routes (e.g. Nova marketing).
  useEffect(() => {
    const META_ID = 'trust-center-robots-meta';
    let el = document.getElementById(META_ID);
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute('name', 'robots');
      el.id = META_ID;
      document.head.appendChild(el);
    }
    el.setAttribute('content', 'noindex, nofollow');
    return () => {
      const existing = document.getElementById(META_ID);
      if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
    };
  }, []);

  useEffect(() => {
    const META_ID = 'trust-center-robots-meta';
    const el = document.getElementById(META_ID);
    if (!el) return;
    // Nova generates the directive automatically — never from customer input.
    // Only a published Trust Center whose workspace has opted in gets to drop
    // the noindex directive. Everything else (draft, unpublished, error,
    // toggle OFF) stays noindex.
    const canIndex =
      !!data &&
      data.allow_search_indexing !== false &&
      !notFound;
    el.setAttribute('content', canIndex ? 'index, follow' : 'noindex, nofollow');
  }, [data, notFound]);
  // --------------------------------------------------------------------------

  const hasSection = useCallback((key) => sectionHas(data, key), [data]);
  const activeKey = SLUG_TO_KEY[sectionSlug] || 'overview';

  useEffect(() => {
    if (!data) return;
    // Retired route: /controls now lives inside Compliance. Preserve deep links.
    if (sectionSlug === 'controls') {
      const qs = searchParams.toString();
      navigate(`/t/${slug}/compliance${qs ? `?${qs}` : ''}`, { replace: true });
      return;
    }
    if (sectionSlug && (!SLUG_TO_KEY[sectionSlug] || !hasSection(SLUG_TO_KEY[sectionSlug]))) {
      navigate(`/t/${slug}`, { replace: true });
    }
  }, [data, sectionSlug, hasSection, navigate, slug, searchParams]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]" data-testid="public-trust-loading"><Loader2 className="w-6 h-6 animate-spin text-[#0F62FE]" /></div>;
  if (notFound || !data) return <TrustCenterUnavailable />;

  return (
    <TrustCenterView data={data} mode="public" slug={slug} activeKey={activeKey}
      frameworkParam={searchParams.get('framework') || ''}
      setFrameworkParam={(token) => setSearchParams(token ? { framework: token } : {}, { replace: false })}
      categoryParam={searchParams.get('category') || ''}
      setCategoryParam={(cat) => setSearchParams(cat ? { category: cat } : {}, { replace: false })}
      onNavigate={(key, opts) => {
        const qp = new URLSearchParams();
        if (opts && opts.framework) qp.set('framework', opts.framework);
        if (opts && opts.category) qp.set('category', opts.category);
        // Search-result deep-links carry `doc` so the destination
        // Resources view can highlight the specific resource card.
        if (opts && opts.doc) qp.set('doc', opts.doc);
        const qs = qp.toString();
        navigate(`/t/${slug}/${KEY_TO_SLUG[key]}${qs ? `?${qs}` : ''}`);
      }} />
  );
};

// Section-availability check shared by the wrapper + the presentational view.
const sectionHas = (data, key) => {
  if (!data) return false;
  const sec = data.sections || {};
  if (!sec[key]) return false;
  switch (key) {
    case 'overview': return true;
    case 'security': {
      // Backend _build_public_security_profile + frontend buildPublicData both
      // strip empty/disabled sections before emitting, so a non-empty map is
      // the single source of truth for "does this workspace have security
      // content to show?". Keep this predicate cheap and drift-free.
      return Object.keys(data.security_profile || {}).length > 0;
    }
    case 'compliance': return ((data.frameworks || []).length > 0) || ((data.compliance_snapshot || []).length > 0);
    case 'resources': return (data.documents || []).length > 0;
    case 'subprocessors': return (data.subprocessors || []).length > 0;
    case 'faq': return (data.faqs || []).length > 0;
    case 'contact': return !!data.security_contact_email || !!(data.disclosure?.statement || '').trim();
    default: return false;
  }
};

/* ============================ The single, shared Trust Center renderer ============================ */
// Public page and the admin Live Preview BOTH render this. Only the data source + nav differ.
export const TrustCenterView = ({ data, mode = 'public', slug: slugProp, activeKey: activeKeyProp, onNavigate, frameworkParam, setFrameworkParam, categoryParam, setCategoryParam }) => {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [reqOpen, setReqOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', company: '', job_title: '', message: '', requested_items: [] });
  // Preview mode has no URL — keep filters in memory so Live Preview works.
  const [previewFramework, setPreviewFramework] = useState('');
  const [previewCategory, setPreviewCategory] = useState('');

  const slug = slugProp || data?.slug || '';
  const has = useCallback((key) => sectionHas(data, key), [data]);
  const navItems = useMemo(() => SECTIONS.filter((s) => has(s.key)), [has]);
  const rawActive = activeKeyProp || 'overview';
  const activeKey = has(rawActive) ? rawActive : 'overview';

  const isPublic = mode === 'public';
  const fwParam = isPublic ? (frameworkParam || '') : previewFramework;
  const setFwParam = isPublic ? (setFrameworkParam || (() => {})) : setPreviewFramework;
  const catParam = isPublic ? (categoryParam || '') : previewCategory;
  const setCatParam = isPublic ? (setCategoryParam || (() => {})) : setPreviewCategory;

  const navigate = (key, opts) => {
    if (opts && 'framework' in opts && !isPublic) setPreviewFramework(opts.framework || '');
    if (opts && 'category' in opts && !isPublic) setPreviewCategory(opts.category || '');
    if (onNavigate) onNavigate(key, opts);
    setMobileNavOpen(false);
  };

  // Request Center — the single consolidated request experience. Accepts
  // strings (free-text asks) or document objects ({id, title, access}).
  const openRequest = (items = []) => {
    const list = (Array.isArray(items) ? items : [items])
      .filter(Boolean)
      .map((it) => (typeof it === 'string' ? { title: it } : { id: it.id, title: it.title, access: it.access }))
      .filter((it) => (it.title || '').trim());
    if (mode === 'public' && slug) {
      list.forEach((t) => trackEvent(slug, 'resource_view', { target_name: (t.title || '').slice(0, 120) }));
    }
    setSubmitted(false);
    setForm((p) => ({ ...p, requested_items: list }));
    setReqOpen(true);
  };

  // ⌘K / Ctrl+K global search hotkey — only in public mode. Admin preview
  // gets its own explicit "Preview search as a buyer" button in its host.
  useEffect(() => {
    if (mode !== 'public') return undefined;
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mode]);

  // Resolve a search result's `section_anchor` (e.g. `/security#identity-access`
  // or `/resources?category=Reports&doc=<id>`) into an in-app navigate.
  const navigateToAnchor = useCallback((anchor) => {
    if (!anchor) return;
    // Parse into path + query + fragment
    let [pathAndQuery, hash] = anchor.split('#');
    const [pathPart, queryPart] = (pathAndQuery || '').split('?');
    const sectionKey = (pathPart || '').replace(/^\//, '') || 'overview';
    const opts = {};
    if (queryPart) {
      const usp = new URLSearchParams(queryPart);
      // Whitelist the query params search result anchors are allowed to
      // carry. `doc` deep-links to a specific resource card so the buyer
      // lands directly on the requested doc, not just the category.
      ['framework', 'category', 'doc'].forEach((k) => {
        const v = usp.get(k);
        if (v) opts[k] = v;
      });
    }
    navigate(sectionKey, opts);
    // Scroll to fragment after a tick — the destination section re-renders first.
    if (hash) {
      setTimeout(() => {
        const el = document.getElementById(hash);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    }
  }, [navigate]);

  const submitRequest = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { toast.error('Please provide your name and work email.'); return; }
    if (mode === 'preview') { toast.info('This is a preview — document requests are disabled here.'); return; }
    setSubmitting(true);
    try {
      const items = form.requested_items || [];
      await axios.post(`/api/public/trust/${slug}/access-request`, {
        name: form.name, email: form.email, company: form.company, job_title: form.job_title, message: form.message,
        requested_documents: items.map((i) => i.title),
        document_ids: items.filter((i) => i.id).map((i) => i.id),
        nda_required: items.some((i) => i.access === 'nda'),
      });
      if (mode === 'public' && slug) {
        trackEvent(slug, 'documentation_request', { target_name: items.map((i) => i.title).join(', ').slice(0, 200) });
      }
      setSubmitted(true);
      setForm({ name: '', email: '', company: '', job_title: '', message: '', requested_items: [] });
    } catch (err) { toast.error(err.response?.data?.detail || 'Something went wrong.'); }
    finally { setSubmitting(false); }
  };

  // Analytics tracking — public mode only. Preview mode never emits events.
  // These hooks must be called on every render (no-op when data/slug/mode absent).
  useEffect(() => {
    if (mode !== 'public' || !slug || !activeKey || !data) return;
    trackEvent(slug, 'page_view', { target_id: activeKey, target_name: SECTION_LABEL[activeKey] || activeKey });
  }, [mode, slug, activeKey, data]);

  useEffect(() => {
    if (mode !== 'public' || !slug || !fwParam || !data) return;
    trackEvent(slug, 'framework_view', {
      framework_token: fwParam,
      target_id: fwParam,
      target_name: frameworkLabelFromToken(fwParam),
    });
  }, [mode, slug, fwParam, data]);

  // Public sanitiser — strip internal/test artefacts + downweight
  // low-signal KPIs. Applied once here so every downstream view (Overview,
  // Resources, Controls, Subprocessors, FAQ, Updates) sees the same clean
  // dataset without needing per-view filters.
  const sanitizedData = useMemo(() => {
    if (!data) return data;
    return {
      ...data,
      documents: (data.documents || []).filter((d) => !isInternalDoc(d)),
      controls: (data.controls || []).filter((c) => !isInternalControl(c)),
      recent_updates: (data.recent_updates || []).filter((u) => !isInternalUpdate(u)),
      faqs: (data.faqs || []).filter((f) => !isInternalFaq(f)),
      subprocessors: (data.subprocessors || []).filter((s) => !isInternalSubprocessor(s)),
      kpis: (data.kpis || []).filter((k) => {
        if (HIDDEN_KPI_KEYS.has(k?.key)) return false;
        // Do not publish an in-progress / draft / planning audit as a KPI.
        // Buyers should only see completed audit statements on the public page.
        if (k?.key === 'last_audit') {
          const s = String(k?.sublabel || '').toLowerCase().replace(/[\s_-]+/g, '');
          if (['inprogress', 'draft', 'planning', 'scheduled', 'notstarted', 'notyetstarted'].includes(s)) {
            return false;
          }
        }
        return true;
      }),
    };
  }, [data]);

  if (!data) return null;

  const T = accentTokens(data.theme?.accent_color || '#0F62FE');
  const { accent, text: aText, onAccent: onAcc } = T;
  const initials = (data.company_name || 'T').trim().slice(0, 2).toUpperCase();
  const baseUrl = `${window.location.origin}/t/${slug}`;
  const ctx = { data: sanitizedData, accent, aText, onAcc, slug, baseUrl, openRequest, has, mode, navigate, frameworkParam: fwParam, setFrameworkParam: setFwParam, categoryParam: catParam, setCategoryParam: setCatParam };

  const Logo = (
    <>
      {data.theme?.logo_url ? <img src={data.theme.logo_url} alt={data.company_name} className="h-8 w-auto" />
        : <div className="w-9 h-9 rounded-lg flex items-center justify-center text-xs font-bold" style={{ backgroundColor: accent, color: onAcc }}>{initials}</div>}
      <span className="font-semibold text-[15px]">{data.company_name || 'Your Company'}</span>
    </>
  );

  return (
    <div className="min-h-full bg-[#FAFAFA] text-[#0A0A0A]" data-testid={mode === 'preview' ? 'tc-preview-view' : 'public-trust-page'}>
      <header className="border-b border-[#E2E8F0] bg-white sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center gap-3">
          {mode === 'public' ? (
            <Link to={`/t/${slug}`} className="flex items-center gap-3 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F62FE]/40" aria-label={`${data.company_name} Trust Center home`}>{Logo}</Link>
          ) : (
            <button type="button" onClick={() => navigate('overview')} className="flex items-center gap-3 rounded-lg focus:outline-none">{Logo}</button>
          )}
          <span className="ml-1.5 hidden sm:inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-[#64748B] font-semibold">
            <ShieldCheck className="w-3.5 h-3.5" style={{ color: aText }} /> Trust Center
          </span>
          <a href={NOVA_URL} target="_blank" rel="noreferrer" data-testid="public-nova-badge"
            className="ml-2 hidden md:inline-flex items-center gap-1.5 text-[10px] font-semibold text-[#94A3B8] hover:text-[#0F62FE] border border-[#E2E8F0] rounded-full px-2 py-0.5 transition-colors" title="Powered by Nova">
            <NovaMark className="w-3 h-3" /> Powered by Nova
          </a>
          <div className="ml-auto flex items-center gap-2">
            <SearchTrigger onClick={() => setSearchOpen(true)} testid="public-search-trigger" />
            <button onClick={() => openRequest()} data-testid="public-request-docs"
              className="hidden sm:inline-flex px-4 py-2 rounded-full text-[11px] uppercase tracking-[0.16em] font-semibold hover:opacity-90 transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#0F62FE]/50" style={{ backgroundColor: accent, color: onAcc }}>Request Documentation</button>
            <button className="md:hidden p-2 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F62FE]/40" onClick={() => setMobileNavOpen((v) => !v)} data-testid="public-mobile-nav-toggle" aria-label="Toggle navigation" aria-expanded={mobileNavOpen}>
              {mobileNavOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        {mobileNavOpen && (
          <div className="md:hidden border-t border-[#E2E8F0] bg-white px-3 py-2" data-testid="public-mobile-nav">
            {navItems.map((n) => (
              <NavTo key={n.key} ctx={ctx} sectionKey={n.key} testid={`public-mnav-${n.key}`}
                className={`block w-full text-left px-3 py-2.5 text-sm font-medium rounded-lg ${activeKey === n.key ? 'bg-[#F1F5F9] text-[#0A0A0A]' : 'text-[#334155] hover:bg-[#F1F5F9]'}`}>{n.label}</NavTo>
            ))}
          </div>
        )}
      </header>

      <nav className="hidden md:block sticky top-16 z-30 bg-white/95 backdrop-blur border-b border-[#E2E8F0]" data-testid="public-tab-nav" aria-label="Trust Center sections">
        <div className="max-w-6xl mx-auto px-6 flex items-center gap-1 overflow-x-auto">
          {navItems.map((n) => (
            <NavTo key={n.key} ctx={ctx} sectionKey={n.key} testid={`public-tab-${n.key}`}
              className={`whitespace-nowrap px-3.5 py-3 text-[13px] font-semibold border-b-2 -mb-px transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F62FE]/30 rounded-t ${activeKey === n.key ? 'border-current' : 'border-transparent text-[#64748B] hover:text-[#0A0A0A]'}`}
              style={activeKey === n.key ? { color: aText } : undefined}>{n.label}</NavTo>
          ))}
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-6 py-10 md:py-12">
        {activeKey === 'overview' && <OverviewView ctx={ctx} navItems={navItems} />}
        {activeKey === 'security' && <SecurityView ctx={ctx} />}
        {activeKey === 'compliance' && <ComplianceView ctx={ctx} />}
        {activeKey === 'resources' && <ResourcesView ctx={ctx} />}
        {activeKey === 'subprocessors' && <SubprocessorsView ctx={ctx} />}
        {activeKey === 'faq' && <FAQView ctx={ctx} />}
        {activeKey === 'contact' && <ContactView ctx={ctx} />}
      </main>

      <footer className="border-t border-[#E2E8F0] bg-white mt-8" data-testid="public-footer">
        <div className="max-w-6xl mx-auto px-6 py-10 grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="md:col-span-2">
            <a href={NOVA_URL} target="_blank" rel="noreferrer" data-testid="public-footer-nova"
              className="inline-flex items-center gap-2 text-[#475569] hover:text-[#0F62FE] transition-colors group">
              <NovaMark className="w-5 h-5" />
              <span className="text-sm font-semibold">Trust Center powered by <span className="font-label">Nova</span></span>
            </a>
            <p className="text-xs text-[#94A3B8] mt-3 max-w-md">
              This Trust Center provides transparency into {data.company_name}'s security, privacy, and compliance program.
            </p>
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-3">Trust</p>
            <ul className="space-y-2 text-sm">
              {ctx.has('compliance') && <li><NavTo ctx={ctx} sectionKey="compliance" testid="public-footer-compliance" className="text-[#475569] hover:text-[#0A0A0A]">Compliance</NavTo></li>}
              {data.footer?.privacy_url ? (
                <li><a href={data.footer.privacy_url} target="_blank" rel="noreferrer" className="text-[#475569] hover:text-[#0A0A0A]" data-testid="public-footer-privacy">Privacy</a></li>
              ) : ctx.has('subprocessors') && (
                <li><NavTo ctx={ctx} sectionKey="subprocessors" testid="public-footer-subprocessors" className="text-[#475569] hover:text-[#0A0A0A]">Privacy</NavTo></li>
              )}
              {data.footer?.terms_url && <li><a href={data.footer.terms_url} target="_blank" rel="noreferrer" className="text-[#475569] hover:text-[#0A0A0A]" data-testid="public-footer-terms">Terms</a></li>}
              {data.footer?.accessibility_url && <li><a href={data.footer.accessibility_url} target="_blank" rel="noreferrer" className="text-[#475569] hover:text-[#0A0A0A]" data-testid="public-footer-accessibility">Accessibility</a></li>}
              {data.footer?.status_url && <li><a href={data.footer.status_url} target="_blank" rel="noreferrer" className="text-[#475569] hover:text-[#0A0A0A] inline-flex items-center gap-1" data-testid="public-footer-status">Status Page <ExternalLink className="w-3 h-3" /></a></li>}
            </ul>
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-3">Resources</p>
            <ul className="space-y-2 text-sm">
              {ctx.has('resources') && <li><NavTo ctx={ctx} sectionKey="resources" testid="public-footer-resources" className="text-[#475569] hover:text-[#0A0A0A]">Document library</NavTo></li>}
              {ctx.has('faq') && <li><NavTo ctx={ctx} sectionKey="faq" testid="public-footer-faq" className="text-[#475569] hover:text-[#0A0A0A]">FAQ</NavTo></li>}
              {ctx.has('contact') && <li><NavTo ctx={ctx} sectionKey="contact" testid="public-footer-contact" className="text-[#475569] hover:text-[#0A0A0A]">Contact security</NavTo></li>}
              <li>
                <button onClick={() => openRequest()} data-testid="public-footer-request-docs" className="text-[#475569] hover:text-[#0A0A0A] text-left">
                  {CTA.requestDocs}
                </button>
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-[#F1F5F9] py-4">
          <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-[#94A3B8]">
            <span>© {new Date().getFullYear()} {data.company_name}. All rights reserved.</span>
            {data.last_updated_at && <span data-testid="public-footer-updated">Last updated {fmtDate(data.last_updated_at)}</span>}
          </div>
        </div>
      </footer>

      <RequestCenter {...{ reqOpen, setReqOpen, submitted, submitting, submitRequest, form, setForm, data: sanitizedData, accent, onAcc }} />

      {/* Phase 3.1 — shared Search Overlay. Public gets analytics enabled;
          the admin Live Preview reuses the same component in read-only
          mode (mode='admin') via TrustCenterPreview and passes its own
          endpoint/analyticsEndpoint. */}
      {mode === 'public' && (
        <SearchOverlay
          open={searchOpen}
          onClose={() => setSearchOpen(false)}
          endpoint={`/api/public/trust/${slug}/search`}
          analyticsEndpoint={`/api/public/trust/${slug}/search/analytics`}
          onNavigate={navigateToAnchor}
          openRequest={(prefilledQuery) => openRequest([prefilledQuery])}
          openContact={() => { navigate('contact'); }}
          mode="public"
          placeholder="Search resources, policies, frameworks, FAQs, subprocessors…"
        />
      )}
    </div>
  );
};

/* ============================ shared building blocks ============================ */
const PageHead = ({ accent, eyebrow, title, subtitle, right }) => (
  <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
    <div>
      <p className="text-[12px] font-semibold uppercase tracking-[0.2em] mb-1" style={{ color: accent }}>{eyebrow}</p>
      <h1 className="font-semibold text-2xl md:text-3xl tracking-tight">{title}</h1>
      {subtitle && <p className="text-sm text-[#64748B] mt-1.5 max-w-2xl leading-relaxed">{subtitle}</p>}
    </div>
    {right}
  </div>
);

const statusTone = (s = '') => {
  const t = s.toLowerCase();
  if (/certified|compliant|complete|active|available|live|enforced|passed/.test(t)) return 'green';
  if (/progress|underway|pending|review|monitor|ongoing/.test(t)) return 'blue';
  if (/planned|upcoming|roadmap|scheduled|draft/.test(t)) return 'amber';
  if (/removed|retired|deprecated|inactive|expired|failed/.test(t)) return 'red';
  return 'slate';
};
const TONE = {
  green: 'bg-[#ECFDF5] text-[#047857] border-[#A7F3D0]',
  blue: 'bg-[#EFF6FF] text-[#1D4ED8] border-[#BFDBFE]',
  amber: 'bg-[#FFFBEB] text-[#B45309] border-[#FDE68A]',
  red: 'bg-[#FEF2F2] text-[#B91C1C] border-[#FECACA]',
  slate: 'bg-[#F1F5F9] text-[#475569] border-[#E2E8F0]',
};
const StatusBadge = ({ status }) => {
  if (!status) return <span className="text-xs text-[#94A3B8]">—</span>;
  return <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-0.5 rounded-full border ${TONE[statusTone(status)]}`}><span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />{status}</span>;
};

const AccessBadge = ({ access }) => {
  const m = accessMeta(access);
  const cls = access === 'public'
    ? 'bg-[#ECFDF5] text-[#047857] border-[#A7F3D0]'
    : access === 'nda'
      ? 'bg-[#F8FAFC] text-[#475569] border-[#CBD5E1]'
      : 'bg-[#FEF3F2] text-[#B42318] border-[#FECDCA]';
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${cls}`}>
      {m.locked ? <Lock className="w-3 h-3" /> : <CheckCircle2 className="w-3 h-3" />}{m.label}
    </span>
  );
};

const Chip = ({ active, onClick, children, accent, testid }) => (
  <button onClick={onClick} data-testid={testid} aria-pressed={active} type="button"
    className={`px-3 py-1.5 rounded-full text-[12px] font-semibold border transition-all duration-[var(--tc-motion,140ms)] tc-focus ${active ? 'text-white shadow-sm' : 'bg-white text-[var(--tc-ink-2)] border-[var(--tc-line)] hover:border-[var(--tc-mute-soft)] hover:bg-[var(--tc-surface-2)]'}`}
    style={active ? { backgroundColor: accent, borderColor: accent } : undefined}>{children}</button>
);

const SearchBox = ({ q, setQ, label = 'Search', testid = 'public-search' }) => (
  <div className="relative w-full md:w-64">
    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--tc-mute-soft)] pointer-events-none" />
    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`${label}…`} aria-label={label} data-testid={testid}
      className="w-full pl-9 pr-3 h-9 rounded-[var(--tc-radius)] border border-[var(--tc-line)] text-[13px] bg-white focus:outline-none focus:border-[var(--tc-blue)] focus:ring-2 focus:ring-[color:var(--tc-blue)]/15 transition-colors" />
  </div>
);

const EmptyState = ({ icon: Icon = FileText, title, hint, action, testid = 'public-empty' }) => (
  <div className="rounded-xl border border-dashed border-[#CBD5E1] bg-white py-14 px-6 text-center" data-testid={testid}>
    <Icon className="w-10 h-10 text-[#CBD5E1] mx-auto mb-3" strokeWidth={1.5} />
    <h3 className="font-semibold text-[#0A0A0A]">{title}</h3>
    {hint && <p className="text-sm text-[#64748B] mt-1.5 max-w-md mx-auto leading-relaxed">{hint}</p>}
    {action && <div className="mt-4 flex justify-center">{action}</div>}
  </div>
);

const RelatedDocs = ({ docs, openRequest, accent, aText }) => {
  if (!docs || docs.length === 0) return <span className="text-xs text-[#94A3B8]">No linked documents</span>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {docs.slice(0, 4).map((d) => (
        <button key={d.id} onClick={() => openRequest(d.title)} title={`Request ${d.title}`}
          className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full border border-[#E2E8F0] bg-white hover:bg-[#F8FAFC] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F62FE]/30 transition-colors"
          style={{ color: aText }}><FileText className="w-3 h-3" />{d.title}</button>
      ))}
    </div>
  );
};

const CrossLinks = ({ ctx, current, targets }) => {
  const { has, aText } = ctx;
  const links = targets.filter((k) => k !== current && has(k));
  if (links.length === 0) return null;
  return (
    <div className="mt-10 pt-6 border-t border-[#E2E8F0]" data-testid="public-crosslinks">
      <p className="text-[11px] uppercase tracking-wider text-[#94A3B8] font-semibold mb-3">Explore related sections</p>
      <div className="flex flex-wrap gap-2">
        {links.map((k) => (
          <NavTo key={k} ctx={ctx} sectionKey={k} testid={`public-xlink-${k}`}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-[#E2E8F0] bg-white text-sm font-medium text-[#334155] hover:bg-[#F8FAFC] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F62FE]/30 transition-colors">
            {SECTION_LABEL[k]} <ArrowRight className="w-3.5 h-3.5" style={{ color: aText }} />
          </NavTo>
        ))}
      </div>
    </div>
  );
};

const PageActions = ({ ctx, items, requestLabel = CTA.request }) => {
  const { accent, onAcc, data, openRequest } = ctx;
  return (
    <div className="mt-7 flex flex-wrap gap-3" data-testid="public-page-actions">
      <button onClick={() => openRequest(items)} data-testid="public-action-request"
        className="px-6 py-3 rounded-full text-xs uppercase tracking-[0.16em] font-semibold hover:opacity-90 transition-opacity inline-flex items-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#0F62FE]/50" style={{ backgroundColor: accent, color: onAcc }}>
        <Send className="w-3.5 h-3.5" /> {requestLabel}
      </button>
      {data.security_contact_email && (
        <a href={`mailto:${data.security_contact_email}`} data-testid="public-action-contact"
          className="px-6 py-3 rounded-full text-xs uppercase tracking-[0.16em] font-semibold border border-[#CBD5E1] hover:bg-white transition-colors inline-flex items-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F62FE]/30">
          <Mail className="w-3.5 h-3.5" /> {CTA.contact}
        </a>
      )}
    </div>
  );
};

/* ============================ Overview ============================ */
// Trust at a glance — admin-curated indicators (max 8). Falls back to the
// framework list so existing tenants get meaningful badges before curating.
const TrustAtAGlance = ({ ctx }) => {
  const { data, aText, navigate, has } = ctx;
  let items = (data.trust_indicators || []).slice(0, 8);
  if (items.length === 0) {
    items = (data.frameworks || []).slice(0, 8).map((f) => ({ id: `fw-${f.name}`, label: f.name, target: 'compliance', target_query: f.name }));
  }
  if (items.length === 0) return null;
  // Deep-link resolver: an indicator can carry a target_query that focuses
  // the destination page (framework panel on Compliance, category filter on
  // Resources). Empty target_query = default section landing.
  const goTo = (t) => {
    if (!t.target) return;
    if (t.target === 'compliance' && t.target_query) {
      navigate('compliance', { framework: t.target_query });
    } else if (t.target === 'resources' && t.target_query) {
      navigate('resources', { category: t.target_query });
    } else {
      navigate(t.target);
    }
  };
  return (
    <section className="border-t border-[#E2E8F0] pt-8" data-testid="public-trust-glance">
      <p className="text-[11px] uppercase tracking-wider text-[#94A3B8] font-semibold mb-4">Trust at a glance</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
        {items.map((t) => {
          const clickable = !!(t.target && has(t.target));
          const inner = (
            <>
              <CheckCircle2 className="w-4 h-4 shrink-0" style={{ color: aText }} />
              <span className="text-[13px] font-medium text-[#0A0A0A] min-w-0 truncate">{t.label}</span>
              {clickable && <ArrowRight className="w-3.5 h-3.5 ml-auto shrink-0 text-[#CBD5E1]" />}
            </>
          );
          const cls = 'flex items-center gap-2.5 rounded-xl border border-[#E2E8F0] bg-white px-4 py-3 w-full text-left';
          return clickable ? (
            <button key={t.id || t.label} type="button" onClick={() => goTo(t)} data-testid={`public-indicator-${(t.label || '').replace(/\s+/g, '-').toLowerCase()}`}
              className={`${cls} hover:border-[#CBD5E1] hover:bg-[#F8FAFC] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F62FE]/30`}>{inner}</button>
          ) : (
            <div key={t.id || t.label} data-testid={`public-indicator-${(t.label || '').replace(/\s+/g, '-').toLowerCase()}`} className={cls}>{inner}</div>
          );
        })}
      </div>
    </section>
  );
};

// Security questionnaire coverage — presence-driven map of the areas most
// security reviews ask about, each deep-linking into its section.
const QuestionnaireCoverage = ({ ctx }) => {
  const { data, aText, navigate, has } = ctx;
  const docs = data.documents || [];
  const catCount = (cat) => docs.filter((d) => (d.category || '') === cat && !d.is_policy).length;
  const policyCount = docs.filter((d) => d.is_policy || d.category === 'Policy').length;
  const areas = [
    has('compliance') && { key: 'compliance', icon: BadgeCheck, label: 'Compliance & certifications', meta: `${(data.frameworks || []).length} framework${(data.frameworks || []).length === 1 ? '' : 's'}`, nav: () => navigate('compliance') },
    policyCount > 0 && { key: 'policies', icon: FileText, label: 'Policies', meta: `${policyCount} published`, nav: () => navigate('resources', { category: 'policies' }) },
    catCount('Security') > 0 && { key: 'security', icon: ShieldCheck, label: 'Security documentation', meta: `${catCount('Security')} resources`, nav: () => navigate('resources', { category: 'security' }) },
    catCount('Privacy') > 0 && { key: 'privacy', icon: Database, label: 'Data privacy', meta: `${catCount('Privacy')} resources`, nav: () => navigate('resources', { category: 'privacy' }) },
    catCount('AI Governance') > 0 && { key: 'ai', icon: Cpu, label: 'AI governance', meta: `${catCount('AI Governance')} resources`, nav: () => navigate('resources', { category: 'ai-governance' }) },
    has('subprocessors') && { key: 'subprocessors', icon: Server, label: 'Subprocessors', meta: `${(data.subprocessors || []).length} vendors`, nav: () => navigate('subprocessors') },
    has('faq') && { key: 'faq', icon: HelpCircle, label: 'FAQs', meta: `${(data.faqs || []).length} answered`, nav: () => navigate('faq') },
    has('contact') && { key: 'contact', icon: Mail, label: 'Security contact & disclosure', meta: 'Direct line', nav: () => navigate('contact') },
  ].filter(Boolean);
  if (areas.length < 2) return null;
  return (
    <section className="border-t border-[#E2E8F0] pt-10" data-testid="public-questionnaire-coverage">
      <div className="mb-5">
        <h2 className="font-semibold text-xl md:text-2xl tracking-tight text-[#0A0A0A]">Security questionnaire coverage</h2>
        <p className="text-sm text-[#64748B] mt-1.5 max-w-2xl leading-relaxed">The areas most security reviews ask about — answered here, self-serve.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {areas.map((a) => (
          <button key={a.key} type="button" onClick={a.nav} data-testid={`public-coverage-${a.key}`}
            className="group rounded-xl border border-[#E2E8F0] bg-white p-4 text-left hover:border-[#CBD5E1] hover:bg-[#F8FAFC] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F62FE]/30">
            <a.icon className="w-5 h-5 mb-2.5" style={{ color: aText }} />
            <div className="text-sm font-semibold text-[#0A0A0A] leading-snug">{a.label}</div>
            <div className="text-xs text-[#94A3B8] mt-1 flex items-center gap-1">
              {a.meta}
              <ArrowRight className="w-3 h-3 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: aText }} />
            </div>
          </button>
        ))}
      </div>
    </section>
  );
};

// Section heading used across the documentation-style overview.
const OverviewSection = ({ id, title, desc, viewKey, viewLabel, ctx, children }) => (
  <section className="border-t border-[#E2E8F0] pt-10" data-testid={id}>
    <div className="flex items-end justify-between gap-4 mb-5">
      <div>
        <h2 className="font-semibold text-xl md:text-2xl tracking-tight text-[#0A0A0A]">{title}</h2>
        {desc && <p className="text-sm text-[#64748B] mt-1.5 max-w-2xl leading-relaxed">{desc}</p>}
      </div>
      {viewKey && ctx.has(viewKey) && (
        <NavTo ctx={ctx} sectionKey={viewKey} testid={`${id}-viewall`}
          className="shrink-0 text-[13px] font-semibold inline-flex items-center gap-1 hover:gap-1.5 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F62FE]/30 rounded" style={{ color: ctx.aText }}>
          {viewLabel} <ArrowRight className="w-3.5 h-3.5" />
        </NavTo>
      )}
    </div>
    {children}
  </section>
);

const OverviewView = ({ ctx, navItems }) => {
  const { data, accent, aText, onAcc, baseUrl, openRequest, navigate } = ctx;
  const docs = data.documents || [];
  // Important policies lead the Resources preview when present.
  const KEY_POLICY_TYPES = ['Information Security', 'Privacy', 'Incident Response', 'Access Control'];
  const policyDocs = docs.filter((d) => d.is_policy);
  const keyPolicies = policyDocs.filter((d) => KEY_POLICY_TYPES.includes(d.policy_type));
  const previewDocs = [...keyPolicies, ...docs.filter((d) => !keyPolicies.includes(d))].slice(0, 5);
  const disc = data.disclosure || {};

  return (
    <div className="space-y-2" data-testid="public-overview">
      {/* Hero band */}
      <section className="pb-9" data-testid="public-overview-hero">
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] px-3 py-1 rounded-full border" style={{ color: aText, borderColor: `${accent}40`, backgroundColor: `${accent}0A` }}>
            <ShieldCheck className="w-3.5 h-3.5" /> Trust &amp; Security
          </span>
          {data.last_updated_at && <span className="inline-flex items-center gap-1.5 text-xs text-[#64748B]" data-testid="public-overview-lastupdated"><Clock className="w-3.5 h-3.5" /> Last updated {fmtDate(data.last_updated_at)}</span>}
        </div>
        <h1 className="font-semibold text-3xl md:text-5xl tracking-[-0.02em] leading-[1.08] text-[#0A0A0A] max-w-4xl">{data.hero?.headline || `${data.company_name} Trust Center`}</h1>
        <p className="text-[#475569] text-lg md:text-xl leading-relaxed max-w-3xl mt-5">{data.trust_statement || data.hero?.subtext}</p>
        <div className="flex flex-wrap gap-3 mt-8">
          <button onClick={() => openRequest()} data-testid="public-quick-request" className="px-6 py-3 rounded-full text-xs uppercase tracking-[0.16em] font-semibold hover:opacity-90 transition-opacity inline-flex items-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#0F62FE]/50" style={{ backgroundColor: accent, color: onAcc }}><Send className="w-3.5 h-3.5" /> Request Documentation</button>
          {data.security_contact_email && <a href={`mailto:${data.security_contact_email}`} data-testid="public-quick-contact" className="px-6 py-3 rounded-full text-xs uppercase tracking-[0.16em] font-semibold border border-[#CBD5E1] hover:bg-[#F8FAFC] transition-colors inline-flex items-center gap-2"><Mail className="w-3.5 h-3.5" /> Contact Security</a>}
          <button onClick={() => copyText(baseUrl)} data-testid="public-quick-copy" className="px-6 py-3 rounded-full text-xs uppercase tracking-[0.16em] font-semibold border border-[#CBD5E1] hover:bg-[#F8FAFC] transition-colors inline-flex items-center gap-2"><Copy className="w-3.5 h-3.5" /> Copy Link</button>
        </div>
      </section>

      {/* Trust at a glance — meaningful indicators, never vanity counters */}
      <TrustAtAGlance ctx={ctx} />

      {/* Security questionnaire coverage */}
      <QuestionnaireCoverage ctx={ctx} />

      {/* Compliance — ONLY admin-featured frameworks appear on the Overview.
          The full framework list lives on the Compliance page. */}
      {(data.overview_featured_frameworks || []).length > 0 && ctx.has('compliance') && (
        <OverviewSection id="public-overview-compliance" title="Compliance" desc="The standards and frameworks our security program is aligned to." viewKey="compliance" viewLabel="View compliance" ctx={ctx}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {(data.overview_featured_frameworks || []).map((fw) => (
              <button key={fw.name} type="button" onClick={() => navigate('compliance', { framework: frameworkToken(fw.name) })}
                data-testid={`public-overview-fw-${fw.name.replace(/\s+/g, '-').toLowerCase()}`}
                className="rounded-xl border border-[#E2E8F0] bg-white p-4 text-left hover:border-[#CBD5E1] hover:bg-[#F8FAFC] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F62FE]/30">
                <div className="flex items-center gap-2 mb-2">
                  <BadgeCheck className="w-5 h-5 shrink-0" style={{ color: aText }} />
                  <span className="font-semibold text-sm text-[#0A0A0A] truncate">{fw.name}</span>
                </div>
                <StatusBadge status={fw.audit_status || fw.status || 'In Progress'} />
                {fw.last_reviewed && <div className="text-[11px] text-[#94A3B8] mt-2">Last reviewed {fmtDate(fw.last_reviewed)}</div>}
              </button>
            ))}
          </div>
        </OverviewSection>
      )}

      {/* Security sections — ONLY admin-featured sections appear on the Overview. */}
      {(data.overview_featured_sections || []).length > 0 && (
        <OverviewSection id="public-overview-security-sections" title="Security highlights" desc="Key areas of our security program." viewKey="security" viewLabel="View security" ctx={ctx}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
            {(data.overview_featured_sections || []).map((s) => (
              <div key={s.key} data-testid={`public-overview-secsection-${s.key}`}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-[15px] inline-flex items-center gap-2"><ShieldCheck className="w-4 h-4" style={{ color: aText }} /> {s.title}</h3>
                  <button type="button" onClick={() => navigate('security', { section: s.key })}
                    className="text-xs font-semibold hover:underline inline-flex items-center gap-1" style={{ color: aText }} data-testid={`public-overview-secsection-${s.key}-viewall`}>
                    View <ArrowRight className="w-3 h-3" />
                  </button>
                </div>
                {s.overview && <p className="text-sm text-[#475569] leading-relaxed mb-2 line-clamp-3">{s.overview}</p>}
                {(s.bullets || []).length > 0 && (
                  <ul className="space-y-2">
                    {s.bullets.map((b, bi) => (
                      <li key={bi} className="text-sm text-[#334155] flex items-start gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" style={{ color: aText }} /><span className="min-w-0">{b}</span></li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </OverviewSection>
      )}

      {/* Security program — ONLY explicitly featured categorized Resources.
          No automatic promotion, no fallback summaries, no legacy arrays —
          nothing appears here unless an admin featured it on the Overview. */}
      {(() => {
        const fbc = data.featured_by_category || {};
        const secFeatured = fbc.Security || [];
        const privFeatured = fbc.Privacy || [];
        const aiFeatured = fbc['AI Governance'] || [];
        const compFeatured = fbc.Compliance || [];
        const hasAnything = (
          secFeatured.length + privFeatured.length + aiFeatured.length + compFeatured.length
        ) > 0;
        if (!hasAnything) return null;
        const CatCard = ({ icon, title, items, catSlug, testid }) => (
          items && items.length > 0 ? (
            <div data-testid={testid}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-[15px] inline-flex items-center gap-2">{icon} {title}</h3>
                <button type="button" onClick={() => navigate('resources', { category: catSlug })}
                  className="text-xs font-semibold hover:underline inline-flex items-center gap-1"
                  style={{ color: aText }} data-testid={`${testid}-viewall`}>
                  View all <ArrowRight className="w-3 h-3" />
                </button>
              </div>
              <ul className="space-y-2.5">
                {items.slice(0, 4).map((c) => (
                  <li key={c.id || c.title} className="text-sm text-[#334155] flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" style={{ color: aText }} />
                    <span className="min-w-0">
                      <span className="block font-medium text-[#0A0A0A]">{c.title}</span>
                      {c.description && <span className="block text-xs text-[#64748B] mt-0.5 line-clamp-2">{c.description}</span>}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null
        );
        return (
          <OverviewSection id="public-overview-program" title="Security program" desc="How we protect data across security, privacy, AI governance and compliance.">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
              {secFeatured.length > 0 && (
                <CatCard icon={<ShieldCheck className="w-4 h-4" style={{ color: aText }} />} title="Security" items={secFeatured} catSlug="security" testid="public-overview-security" />
              )}
              {privFeatured.length > 0 && (
                <CatCard icon={<Database className="w-4 h-4" style={{ color: aText }} />} title="Privacy &amp; data handling" items={privFeatured} catSlug="privacy" testid="public-overview-privacy" />
              )}
              {aiFeatured.length > 0 && (
                <CatCard icon={<Cpu className="w-4 h-4" style={{ color: aText }} />} title="AI governance" items={aiFeatured} catSlug="ai-governance" testid="public-overview-aigov" />
              )}
              {compFeatured.length > 0 && (
                <CatCard icon={<BadgeCheck className="w-4 h-4" style={{ color: aText }} />} title="Compliance resources" items={compFeatured} catSlug="compliance" testid="public-overview-compliance-featured" />
              )}
            </div>
          </OverviewSection>
        );
      })()}

      {/* Resources preview */}
      {docs.length > 0 && (
        <OverviewSection id="public-overview-resources" title="Featured resources" desc="Security and compliance documentation available to reviewers. Browse the full library to see everything." viewKey="resources" viewLabel="Browse library" ctx={ctx}>
          <ul className="rounded-xl border border-[#E2E8F0] bg-white divide-y divide-[#F1F5F9]">
            {previewDocs.map((d) => (
              <li key={d.id} className="flex items-center justify-between gap-3 px-4 py-3.5" data-testid={`public-overview-doc-${d.id}`}>
                <span className="min-w-0 flex items-center gap-2.5">
                  <FileText className="w-4 h-4 text-[#94A3B8] shrink-0" />
                  <span className="min-w-0">
                    <span className="text-sm font-medium text-[#0A0A0A] block truncate">{d.title}</span>
                    <span className="text-xs text-[#94A3B8] flex items-center gap-2 flex-wrap">
                      <AccessBadge access={d.access} />
                      {d.version > 0 && <span>v{d.version}</span>}
                      {d.last_updated && <span>· Updated {fmtDate(d.last_updated)}</span>}
                    </span>
                  </span>
                </span>
                <DocActions ctx={ctx} d={d} variant="link" testid={`public-overview-doc-action-${d.id}`} />
              </li>
            ))}
          </ul>
          {policyDocs.length > 0 && (
            <div className="mt-3">
              <button type="button" onClick={() => navigate('resources', { category: 'policies' })} data-testid="public-overview-viewall-policies"
                className="text-[13px] font-semibold inline-flex items-center gap-1 hover:gap-1.5 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F62FE]/30 rounded" style={{ color: aText }}>
                View all policies <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </OverviewSection>
      )}

      {/* Recent updates timeline */}
      {(data.recent_updates || []).length > 0 && (
        <OverviewSection id="public-recent-updates" title="Recent updates" desc="A timeline of changes to our security posture and documentation.">
          <ol className="relative border-l border-[#E2E8F0] ml-1.5 space-y-6">
            {data.recent_updates.slice(0, 4).map((u) => (
              <li key={u.id} className="pl-6" data-testid={`public-update-${u.id}`}>
                <span className="absolute -left-[5px] w-2.5 h-2.5 rounded-full" style={{ backgroundColor: accent }} />
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <span className="text-sm font-semibold text-[#0A0A0A]">{u.title}</span>
                  <span className="text-xs text-[#94A3B8]">{fmtDate(u.date)}</span>
                </div>
                {u.description && <p className="text-sm text-[#64748B] mt-1 leading-relaxed">{u.description}</p>}
              </li>
            ))}
          </ol>
        </OverviewSection>
      )}

      {/* Contact */}
      <OverviewSection id="public-overview-contact" title="Contact security" desc="Reviewing our security for a vendor assessment? Request documentation or reach out directly.">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="rounded-xl border border-[#E2E8F0] bg-white p-5" data-testid="public-contact-security">
            <div className="text-xs uppercase tracking-wider text-[#94A3B8] font-semibold mb-2 inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> Security contact</div>
            {data.security_contact_email
              ? <a href={`mailto:${data.security_contact_email}`} className="text-sm font-medium hover:underline min-w-0 break-words [overflow-wrap:anywhere]" style={{ color: aText }} data-testid="public-overview-contact-email">{data.security_contact_email}</a>
              : <span className="text-sm text-[#64748B]">Use Request Documentation to reach our team.</span>}
          </div>
          <div className="rounded-xl border border-[#E2E8F0] bg-white p-5" data-testid="public-contact-disclosure">
            <div className="text-xs uppercase tracking-wider text-[#94A3B8] font-semibold mb-2 inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Responsible disclosure</div>
            <p className="text-sm text-[#475569] leading-relaxed line-clamp-4">{(disc.statement || '').trim() || 'Report suspected vulnerabilities to our security team; we appreciate responsible disclosure.'}</p>
          </div>
          <div className="rounded-xl border border-[#E2E8F0] bg-white p-5" data-testid="public-contact-sla">
            <div className="text-xs uppercase tracking-wider text-[#94A3B8] font-semibold mb-2 inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Response SLA</div>
            <p className="text-sm text-[#475569] leading-relaxed">{(disc.response_sla || '').trim() || 'We aim to acknowledge inquiries promptly.'}</p>
            <button onClick={() => openRequest()} className="mt-3 text-xs font-semibold hover:underline focus:outline-none inline-flex items-center gap-1" style={{ color: aText }} data-testid="public-overview-contact-request"><Send className="w-3.5 h-3.5" /> Request Documentation</button>
          </div>
        </div>
      </OverviewSection>
    </div>
  );
};

// Slim, single-line summary used on detail pages instead of heavy stat-card grids.
const SummaryLine = ({ items, testid }) => (
  <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-6 pb-5 border-b border-[#E2E8F0]" data-testid={testid}>
    {items.filter(Boolean).map((it, i) => (
      <span key={i} className="inline-flex items-baseline gap-1.5 text-sm">
        <span className="font-semibold text-[#0A0A0A] text-base">{it.value}</span>
        <span className="text-[#64748B]">{it.label}</span>
      </span>
    ))}
  </div>
);

/* ============================ Resources ============================ */
const RESOURCE_FILTERS = ['All', 'Policies', 'Compliance', 'Security', 'Privacy', 'AI Governance', 'Reports', 'Legal', 'Certificates', 'Questionnaires', 'Documentation', 'Other'];
const catSlug = (label) => label.toLowerCase().replace(/\s+/g, '-');
const FILTER_BY_SLUG = Object.fromEntries(RESOURCE_FILTERS.map((f) => [catSlug(f), f]));
const RES_PAGE_SIZE = 12;

// A resource matches a single category chip. Multi-select is OR across chips.
const resMatchOne = (d, filter) => {
  const cat = (d.category || '').toLowerCase();
  const hay = `${d.category} ${d.doc_type} ${d.related_framework} ${d.title} ${(d.tags || []).join(' ')}`.toLowerCase();
  const f = filter.toLowerCase();
  if (filter === 'Policies') return d.is_policy || cat === 'policy' || hay.includes('policy') || hay.includes('policies');
  if (filter === 'Questionnaires') return cat === 'questionnaires' || cat === 'questionnaire' || hay.includes('question');
  if (filter === 'Reports') return cat === 'reports' || hay.includes('report') || hay.includes('certificate');
  if (filter === 'Certificates') return cat === 'certificates' || hay.includes('certificate') || hay.includes('attestation');
  if (filter === 'Documentation') return cat === 'documentation' || hay.includes('documentation') || hay.includes('overview') || hay.includes('whitepaper');
  if (filter === 'Legal') return cat === 'legal' || hay.includes('legal') || hay.includes('dpa') || hay.includes('terms');
  if (filter === 'Other') return !d.is_policy && !['compliance', 'security', 'privacy', 'ai governance', 'policy', 'questionnaire', 'questionnaires', 'legal', 'reports', 'certificates', 'documentation'].includes(cat);
  if (['compliance', 'security', 'privacy', 'ai governance'].includes(f)) return cat === f || hay.includes(f);
  return hay.includes(f);
};

const ResourcesView = ({ ctx }) => {
  const { data, accent, aText, onAcc, baseUrl, openRequest, slug, mode, categoryParam, setCategoryParam, navigate } = ctx;
  // Multi-select category filter — URL is comma-separated slugs so links
  // stay shareable: /resources?category=security,privacy,ai-governance
  const parseSlugs = (raw) => (raw || '')
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean)
    .map((s) => FILTER_BY_SLUG[s])
    .filter(Boolean);
  const activeFilters = parseSlugs(categoryParam);
  const writeFilters = (labels) => setCategoryParam(labels.length ? labels.map(catSlug).join(',') : '');
  const toggleFilter = (label) => {
    if (label === 'All') return writeFilters([]);
    if (activeFilters.includes(label)) writeFilters(activeFilters.filter((x) => x !== label));
    else writeFilters([...activeFilters, label]);
  };
  const clearFilters = () => writeFilters([]);
  const [access, setAccess] = useState('all'); // all | public | request | nda
  // Phase 1 audit-requirements: independent artefact-type filter. Distinct
  // from access — a "Diagram" may be Public, Request, NDA or Internal.
  const [artefactType, setArtefactType] = useState('all');
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(1);
  const docs = data.documents || [];
  useSearchTracking(mode, slug, q, 'resources');
  useEffect(() => { setPage(1); }, [categoryParam, access, q]);

  const matchFilter = (d) => activeFilters.length === 0 || activeFilters.some((f) => resMatchOne(d, f));
  const matchAccess = (d) => access === 'all' || d.access === access;
  const matchArtefact = (d) => artefactType === 'all' || (d.artefact_type || '') === artefactType;
  const rows = docs
    .filter((d) => matchFilter(d) && matchAccess(d) && matchArtefact(d) && `${d.title} ${d.description} ${(d.tags || []).join(' ')}`.toLowerCase().includes(q.toLowerCase()));
  const totalPages = Math.max(1, Math.ceil(rows.length / RES_PAGE_SIZE));
  const [highlightedDocId, setHighlightedDocId] = useState('');
  // Search deep-link support — when ?doc=<id> is present in the URL,
  // scroll the matching resource row into view and briefly highlight it.
  useEffect(() => {
    if (mode !== 'public') return undefined;
    const usp = new URLSearchParams(window.location.search);
    const docId = usp.get('doc');
    if (!docId) return undefined;
    const idx = rows.findIndex((r) => r.id === docId);
    if (idx < 0) return undefined;
    const targetPage = Math.floor(idx / RES_PAGE_SIZE) + 1;
    if (targetPage !== page) setPage(targetPage);
    const t = setTimeout(() => {
      const el = document.getElementById(`res-row-${docId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setHighlightedDocId(docId);
        setTimeout(() => setHighlightedDocId(''), 2000);
      }
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows.length, mode]);
  const curPage = Math.min(Math.max(1, page), totalPages);
  const pageRows = rows.slice((curPage - 1) * RES_PAGE_SIZE, curPage * RES_PAGE_SIZE);

  const toggle = (id) => setSelected((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  const selectedDocs = docs.filter((d) => selected.includes(d.id));
  const resourceLink = (id) => `${baseUrl}/resources?resource=${id}`;
  const countFor = (f) => docs.filter((d) => resMatchOne(d, f)).length;

  const publicCount = docs.filter((d) => d.access === 'public').length;
  const ndaCount = docs.filter((d) => d.access === 'nda').length;
  const gatedCount = docs.length - publicCount - ndaCount;
  const filtersOn = activeFilters.length > 0 || access !== 'all' || artefactType !== 'all' || q.trim().length > 0;

  // Featured band — admin-featured resources (explicit only), hidden while filtering.
  const featured = (() => {
    const seen = new Set();
    const out = [];
    Object.values(data.featured_by_category || {}).forEach((list) => (list || []).forEach((d) => {
      if (d && d.id && !seen.has(d.id)) { seen.add(d.id); out.push(d); }
    }));
    return out.slice(0, 3);
  })();

  const rowCta = (d, testid) => (
    <DocActions ctx={ctx} d={d} variant="button" testid={testid} />
  );

  return (
    <div data-testid="public-resources">
      <PageHead accent={accent} eyebrow="Resources" title="Resource library" subtitle="Security, compliance, privacy and AI governance documentation — available instantly or on request." />

      <SummaryLine testid="public-resources-summary" items={[
        { value: docs.length, label: docs.length === 1 ? 'resource' : 'resources' },
        publicCount > 0 && { value: publicCount, label: 'public' },
        gatedCount > 0 && { value: gatedCount, label: 'on request' },
        ndaCount > 0 && { value: ndaCount, label: 'under NDA' },
      ]} />

      {/* Featured resources — admin-curated */}
      {!filtersOn && featured.length > 0 && (
        <div className="mb-6" data-testid="public-res-featured">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-[var(--tc-mute)] mb-2.5">Featured</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {featured.map((d) => (
              <div key={d.id} className="rounded-xl border border-[#E2E8F0] bg-white p-4 flex flex-col" data-testid={`public-res-featured-${d.id}`}>
                <div className="flex items-center gap-2 mb-1.5">
                  <FileText className="w-4 h-4 shrink-0" style={{ color: aText }} />
                  <span className="text-sm font-semibold text-[#0A0A0A] truncate">{d.title}</span>
                </div>
                {d.access !== 'nda' && d.description && <p className="text-xs text-[#64748B] leading-relaxed line-clamp-2 mb-3">{d.description}</p>}
                <div className="mt-auto flex items-center justify-between gap-2">
                  <AccessBadge access={d.access} />
                  {rowCta(d, `public-res-featured-cta-${d.id}`)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sticky multi-select filter bar */}
      <div className="sticky top-0 z-20 -mx-4 md:-mx-6 px-4 md:px-6 py-3 mb-4 bg-white/90 backdrop-blur-sm border-y border-[var(--tc-line)]" data-testid="public-res-filterbar">
        <div className="flex flex-col lg:flex-row lg:items-center gap-3">
          <div className="flex flex-wrap gap-2 flex-1" role="group" aria-label="Category filters">
            <Chip active={activeFilters.length === 0} onClick={() => clearFilters()} accent={accent} testid="public-res-filter-all">All</Chip>
            {RESOURCE_FILTERS.filter((f) => f !== 'All').map((f) => {
              const n = countFor(f);
              if (n === 0) return null;
              return (
                <Chip key={f} active={activeFilters.includes(f)} onClick={() => toggleFilter(f)} accent={accent} testid={`public-res-filter-${f.replace(/\s+/g, '-').toLowerCase()}`}>
                  {f} <span className="opacity-70 tabular-nums">{n}</span>
                </Chip>
              );
            })}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div className="hidden md:flex items-center gap-1 pr-2 border-r border-[var(--tc-line)]" role="group" aria-label="Access filters">
              {[['all', 'Any access'], ['public', 'Public'], ['request', 'On request'], ...(ndaCount > 0 ? [['nda', 'NDA']] : [])].map(([val, label]) => (
                <button key={val} type="button" onClick={() => setAccess(val)}
                  data-testid={`public-res-access-${val}`}
                  aria-pressed={access === val}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider transition-colors tc-focus ${access === val ? 'text-white' : 'text-[var(--tc-mute)] hover:text-[var(--tc-ink)]'}`}
                  style={access === val ? { backgroundColor: accent } : {}}>{label}</button>
              ))}
            </div>
            {/* Phase 1: Type filter — independent of Access. Only shows
                artefact types actually present in the workspace. */}
            {(() => {
              const typesPresent = Array.from(new Set(docs.map((d) => d.artefact_type).filter(Boolean)));
              if (typesPresent.length === 0) return null;
              return (
                <select value={artefactType} onChange={(e) => setArtefactType(e.target.value)}
                  className="hidden md:inline-flex text-[11px] font-semibold uppercase tracking-wider rounded-full px-2.5 py-1 border border-[var(--tc-line)] bg-white text-[var(--tc-mute)] hover:text-[var(--tc-ink)] tc-focus"
                  aria-label="Filter by artefact type"
                  data-testid="public-res-type-filter">
                  <option value="all">Any type</option>
                  {typesPresent.map((t) => (
                    <option key={t} value={t}>{t.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase())}</option>
                  ))}
                </select>
              );
            })()}
            <SearchBox q={q} setQ={setQ} label="Search resources" testid="public-res-search" />
            {filtersOn && (
              <button type="button" onClick={() => { clearFilters(); setAccess('all'); setArtefactType('all'); setQ(''); }}
                data-testid="public-res-clear-all"
                className="text-[11px] font-semibold uppercase tracking-wider text-[var(--tc-mute)] hover:text-[var(--tc-danger)] px-2 py-1 rounded tc-focus whitespace-nowrap">
                Clear all
              </button>
            )}
          </div>
        </div>
        {filtersOn && (
          <div className="mt-2 flex items-center gap-2 text-[11px] text-[var(--tc-mute)]" data-testid="public-res-active-summary">
            <span className="font-semibold tabular-nums text-[var(--tc-ink-2)]">{rows.length}</span> of <span className="tabular-nums">{docs.length}</span> match your filters
            {activeFilters.length > 0 ? <span aria-hidden="true">·</span> : null}
            {activeFilters.length > 0 && <span data-testid="public-res-active-count">{activeFilters.length} categor{activeFilters.length === 1 ? 'y' : 'ies'}</span>}
          </div>
        )}
      </div>

      {selected.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 mb-3 p-3 rounded-xl border border-[#E2E8F0] bg-[#F8FAFC]" data-testid="public-bulk-bar">
          <span className="text-sm font-medium">{selected.length} selected</span>
          <button onClick={() => openRequest(selectedDocs)} data-testid="public-bulk-request" className="px-4 py-2 rounded-full text-xs uppercase tracking-[0.14em] font-semibold hover:opacity-90 transition-opacity inline-flex items-center gap-1.5" style={{ backgroundColor: accent, color: ctx.onAcc }}><Send className="w-3.5 h-3.5" /> Request Access to Selected</button>
          <button onClick={() => copyText(selected.map(resourceLink).join('\n'))} data-testid="public-bulk-copy" className="px-4 py-2 rounded-full text-xs uppercase tracking-[0.14em] font-semibold border border-[#CBD5E1] hover:bg-white transition-colors inline-flex items-center gap-1.5"><Copy className="w-3.5 h-3.5" /> Copy selected links</button>
          <button onClick={() => setSelected([])} className="text-xs text-[#64748B] hover:text-[#0A0A0A]">Clear</button>
        </div>
      )}

      {docs.length === 0 ? (
        <EmptyState icon={FileText} title="No resources published yet" hint="This company has not published resources here yet. Use the request button to reach their security team." action={<button onClick={() => openRequest()} className="px-5 py-2.5 rounded-full text-xs uppercase tracking-[0.16em] font-semibold" style={{ backgroundColor: accent, color: ctx.onAcc }}>Request documentation</button>} />
      ) : rows.length === 0 ? (
        <EmptyState icon={FileText} title="No resources match your filters" hint="Try clearing a filter or searching for something else — or request what you need directly." action={<button onClick={() => openRequest()} className="px-5 py-2.5 rounded-full text-xs uppercase tracking-[0.16em] font-semibold" style={{ backgroundColor: accent, color: ctx.onAcc }}>Request documentation</button>} />
      ) : (
        <>
          <ul className="rounded-xl border border-[var(--tc-line)] bg-white divide-y divide-[#F1F5F9]" data-testid="public-res-list">
            {pageRows.map((d, i) => {
              const isNda = d.access === 'nda';
              const isHighlighted = d.id === highlightedDocId;
              return (
                <li key={d.id} id={`res-row-${d.id}`}
                  className={`p-4 sm:px-5 flex items-start gap-3 transition-colors ${isHighlighted ? 'bg-[#EFF6FF] ring-2 ring-[#0F62FE]/40' : ''}`}
                  data-testid={`public-res-row-${i}`}>
                  <input type="checkbox" aria-label={`Select ${d.title}`} checked={selected.includes(d.id)} onChange={() => toggle(d.id)} data-testid={`public-res-check-${i}`} className="mt-1.5 shrink-0" />
                  <div className="w-9 h-9 rounded-lg shrink-0 flex items-center justify-center" style={{ backgroundColor: `${accent}0D` }}>
                    <FileText className="w-4.5 h-4.5 w-[18px] h-[18px]" style={{ color: aText }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm text-[#0A0A0A]">{d.title}</span>
                      <AccessBadge access={d.access} />
                      {d.is_policy && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#EEF2FF] text-[#4338CA] font-semibold">{d.policy_type || 'Policy'}</span>}
                    </div>
                    {!isNda && d.description && <p className="text-xs text-[#64748B] mt-1 max-w-2xl leading-relaxed">{d.description}</p>}
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-[11px] text-[#94A3B8]">
                      {isNda ? (
                        <span>{d.doc_type || d.category || 'Document'} · shared under NDA on approval</span>
                      ) : (
                        <>
                          {d.version > 0 && <span data-testid={`public-res-version-${i}`}>v{d.version}</span>}
                          {(d.category || d.doc_type) && <span>{d.is_policy ? (d.policy_type || 'Policy') : (d.category || d.doc_type)}</span>}
                          {(d.related_frameworks || []).slice(0, 3).map((fw) => (
                            <span key={fw} className="inline-flex items-center gap-1"><BadgeCheck className="w-3 h-3" style={{ color: aText }} /> {fw}</span>
                          ))}
                          {d.last_updated && <span>Updated {fmtDate(d.last_updated)}</span>}
                          {d.next_review_date && <span>Review {fmtDate(d.next_review_date)}</span>}
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {rowCta(d, `public-res-request-${i}`)}
                    <button onClick={() => copyText(resourceLink(d.id))} data-testid={`public-res-copy-${i}`} aria-label={`Copy link to ${d.title}`} className="p-1.5 rounded-full border border-[#E2E8F0] text-[#64748B] hover:text-[#0A0A0A] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F62FE]/30 hidden sm:inline-flex" title="Copy link"><Copy className="w-3.5 h-3.5" /></button>
                  </div>
                </li>
              );
            })}
          </ul>
          <div className="flex items-center justify-between gap-3 mt-4 flex-wrap" data-testid="public-res-pagination">
            <span className="text-xs text-[#94A3B8]">
              Showing {(curPage - 1) * RES_PAGE_SIZE + 1}–{Math.min(curPage * RES_PAGE_SIZE, rows.length)} of {rows.length} resource{rows.length === 1 ? '' : 's'}
            </span>
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <button type="button" disabled={curPage <= 1} onClick={() => setPage(curPage - 1)} data-testid="public-res-prev"
                  className="px-3 py-1.5 rounded-full text-xs font-semibold border border-[#E2E8F0] bg-white disabled:opacity-40 hover:bg-[#F8FAFC] transition-colors">← Prev</button>
                <span className="text-xs text-[#64748B] tabular-nums">Page {curPage} of {totalPages}</span>
                <button type="button" disabled={curPage >= totalPages} onClick={() => setPage(curPage + 1)} data-testid="public-res-next"
                  className="px-3 py-1.5 rounded-full text-xs font-semibold border border-[#E2E8F0] bg-white disabled:opacity-40 hover:bg-[#F8FAFC] transition-colors">Next →</button>
              </div>
            )}
          </div>
        </>
      )}

      <CrossLinks ctx={ctx} current="resources" targets={['compliance', 'subprocessors', 'contact']} />
    </div>
  );
};

/* ============================ Compliance ============================ */
// Framework-first compliance experience. Each framework card expands into a
// panel with four evidence groups: Evidence · Policies · Controls · FAQs.
const fwKeywords = (name) => {
  const n = (name || '').toLowerCase();
  if (n.includes('soc')) return ['soc'];
  if (n.includes('iso')) return ['iso'];
  if (n.includes('gdpr')) return ['gdpr'];
  if (n.includes('hipaa')) return ['hipaa'];
  if (n.includes('pci')) return ['pci'];
  const first = n.split(/\s+/)[0];
  return first ? [first] : [];
};

const PanelCard = ({ icon: Icon, title, count, aText, children, testid }) => (
  <div className="rounded-xl border border-[#E2E8F0] bg-white p-5" data-testid={testid}>
    <div className="flex items-center gap-2 mb-3">
      <Icon className="w-4 h-4 shrink-0" style={{ color: aText }} />
      <h3 className="font-semibold text-[15px]">{title}</h3>
      {typeof count === 'number' && <span className="text-xs text-[#94A3B8] tabular-nums">{count}</span>}
    </div>
    {children}
  </div>
);

const FrameworkPanel = ({ ctx, fw, token, evidence, policies, controls, faqs }) => {
  const { accent, aText, onAcc, openRequest, navigate } = ctx;
  const [openDomain, setOpenDomain] = useState('');
  const [openFaq, setOpenFaq] = useState('');

  const domains = useMemo(() => {
    const map = {};
    controls.forEach((c) => {
      const key = (c.category || '').trim() || 'Other';
      (map[key] = map[key] || []).push(c);
    });
    return Object.entries(map).sort((a, b) => b[1].length - a[1].length);
  }, [controls]);

  const DocList = ({ items, prefix }) => (
    <ul className="divide-y divide-[#F1F5F9] -mx-1">
      {items.slice(0, 5).map((d) => (
        <li key={d.id} className="flex items-center gap-2.5 px-1 py-2.5" data-testid={`${prefix}-${d.id}`}>
          <FileText className="w-4 h-4 shrink-0 text-[#94A3B8]" />
          <span className="text-sm text-[#0A0A0A] font-medium min-w-0 truncate flex-1">{d.title}</span>
          <AccessBadge access={d.access} />
          <DocActions ctx={ctx} d={d} variant="link" testid={`${prefix}-action-${d.id}`} />
        </li>
      ))}
      {items.length > 5 && (
        <li className="px-1 py-2.5">
          <button type="button" onClick={() => navigate('resources')} className="text-xs font-semibold inline-flex items-center gap-1 hover:underline" style={{ color: aText }}>
            View all {items.length} in Resources <ArrowRight className="w-3 h-3" />
          </button>
        </li>
      )}
    </ul>
  );

  const groupsCount = [evidence.length, policies.length, controls.length, faqs.length].filter((n) => n > 0).length;
  const label = fw.name;

  return (
    <div className="mt-5 rounded-2xl border border-[#E2E8F0] bg-[#FBFCFE] p-5 md:p-6" data-testid={`public-fw-panel-${token}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
        <div>
          <h2 className="font-semibold text-lg text-[#0A0A0A] inline-flex items-center gap-2">
            <BadgeCheck className="w-5 h-5" style={{ color: aText }} /> {label}
          </h2>
          <div className="flex items-center gap-3 mt-1.5">
            <StatusBadge status={fw.status || 'In Progress'} />
            {fw.last_updated && <span className="text-xs text-[#94A3B8]">Last reviewed {fmtDate(fw.last_updated)}</span>}
          </div>
        </div>
        <button type="button" onClick={() => openRequest([`${label} evidence package`])} data-testid={`public-fw-request-${token}`}
          className="px-4 py-2 rounded-full text-[11px] uppercase tracking-[0.14em] font-semibold hover:opacity-90 transition-opacity inline-flex items-center gap-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#0F62FE]/50"
          style={{ backgroundColor: accent, color: onAcc }}>
          <Send className="w-3 h-3" /> Request {label} evidence
        </button>
      </div>

      {groupsCount === 0 ? (
        <p className="text-sm text-[#64748B]" data-testid={`public-fw-empty-${token}`}>
          Detailed {label} evidence is available on request — use the button above and the security team will follow up.
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {evidence.length > 0 && (
            <PanelCard icon={FileText} title="Evidence" count={evidence.length} aText={aText} testid={`public-fw-evidence-${token}`}>
              <DocList items={evidence} prefix={`public-fw-evidence-${token}`} />
            </PanelCard>
          )}
          {policies.length > 0 && (
            <PanelCard icon={ClipboardCheck} title="Policies" count={policies.length} aText={aText} testid={`public-fw-policies-${token}`}>
              <DocList items={policies} prefix={`public-fw-policies-${token}`} />
            </PanelCard>
          )}
          {controls.length > 0 && (
            <PanelCard icon={ShieldCheck} title="Controls" count={controls.length} aText={aText} testid={`public-fw-controls-${token}`}>
              <p className="text-xs text-[#94A3B8] mb-2">{controls.length.toLocaleString()} requirements mapped across {domains.length} domain{domains.length === 1 ? '' : 's'}.</p>
              <ul className="divide-y divide-[#F1F5F9] -mx-1">
                {domains.map(([name, items]) => {
                  const key = name.toLowerCase().replace(/\s+/g, '-');
                  const isOpen = openDomain === name;
                  return (
                    <li key={name} className="px-1">
                      <button type="button" onClick={() => setOpenDomain(isOpen ? '' : name)} aria-expanded={isOpen}
                        data-testid={`public-fw-domain-${token}-${key}`}
                        className="w-full flex items-center justify-between gap-3 py-2.5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F62FE]/30 rounded">
                        <span className="text-sm font-medium text-[#0A0A0A] min-w-0 truncate">{name}</span>
                        <span className="flex items-center gap-2 shrink-0">
                          <span className="text-xs text-[#94A3B8] tabular-nums">{items.length}</span>
                          <ChevronDown className={`w-4 h-4 text-[#94A3B8] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                        </span>
                      </button>
                      {isOpen && (
                        <ul className="pb-2.5 space-y-1.5">
                          {items.slice(0, 10).map((c, i) => (
                            <li key={c.id || i} className="text-xs text-[#475569] flex items-start gap-1.5 pl-1">
                              <CheckCircle2 className="w-3.5 h-3.5 mt-px shrink-0" style={{ color: aText }} />
                              <span className="min-w-0">{c.title}</span>
                            </li>
                          ))}
                          {items.length > 10 && <li className="text-[11px] text-[#94A3B8] pl-6">+ {items.length - 10} more in this domain</li>}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            </PanelCard>
          )}
          {faqs.length > 0 && (
            <PanelCard icon={HelpCircle} title="Related FAQs" count={faqs.length} aText={aText} testid={`public-fw-faqs-${token}`}>
              <ul className="divide-y divide-[#F1F5F9] -mx-1">
                {faqs.slice(0, 5).map((f) => {
                  const isOpen = openFaq === f.id;
                  return (
                    <li key={f.id} className="px-1">
                      <button type="button" onClick={() => setOpenFaq(isOpen ? '' : f.id)} aria-expanded={isOpen}
                        data-testid={`public-fw-faq-${token}-${f.id}`}
                        className="w-full flex items-center justify-between gap-3 py-2.5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F62FE]/30 rounded">
                        <span className="text-sm font-medium text-[#0A0A0A]">{f.question}</span>
                        <ChevronDown className={`w-4 h-4 shrink-0 text-[#94A3B8] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                      </button>
                      {isOpen && <p className="text-sm text-[#475569] leading-relaxed pb-3">{f.answer}</p>}
                    </li>
                  );
                })}
                {faqs.length > 5 && (
                  <li className="px-1 py-2.5">
                    <button type="button" onClick={() => navigate('faq')} className="text-xs font-semibold inline-flex items-center gap-1 hover:underline" style={{ color: aText }}>
                      View all FAQs <ArrowRight className="w-3 h-3" />
                    </button>
                  </li>
                )}
              </ul>
            </PanelCard>
          )}
        </div>
      )}
    </div>
  );
};

/* ============================ Security (Phase 2) ============================ */
// Single-page hub with 10 anchored sections. Each section is optional —
// empty sections auto-hide. Section content + cross-links are admin-authored
// via the Trust Center Admin Security tab and NEVER auto-published.
const SECURITY_SECTION_ORDER = [
  'identity_access',
  'infrastructure',
  'encryption',
  'application_api_security',
  'logging_monitoring',
  'vulnerability_management',
  'secure_development',
  'business_continuity',
  'data_privacy',
  'ai_governance',
];
const SECURITY_SECTION_ICONS = {
  identity_access: Lock,
  infrastructure: Server,
  encryption: ShieldCheck,
  application_api_security: Cpu,
  logging_monitoring: Activity,
  vulnerability_management: ShieldAlert,
  secure_development: ClipboardCheck,
  business_continuity: Timer,
  data_privacy: Database,
  ai_governance: Sparkles,
};

const SecuritySectionCard = ({ ctx, section }) => {
  const { aText, accent, onAcc, openRequest, navigate } = ctx;
  const Icon = SECURITY_SECTION_ICONS[section.key] || ShieldCheck;
  const anchor = `security-${section.key.replace(/_/g, '-')}`;

  const renderDocRow = (d, kind) => (
    <li key={`${kind}-${d.id}`} className="flex items-center gap-2.5 py-2" data-testid={`public-security-${section.key}-${kind}-${d.id}`}>
      <FileText className="w-4 h-4 shrink-0 text-[#94A3B8]" />
      <span className="text-sm text-[#0A0A0A] font-medium min-w-0 truncate flex-1">{d.title}</span>
      <AccessBadge access={d.access} />
      <DocActions ctx={ctx} d={d} variant="link" testid={`public-security-${section.key}-${kind}-action-${d.id}`} />
    </li>
  );

  return (
    <section id={anchor} className="scroll-mt-32 rounded-2xl border border-[#E2E8F0] bg-white p-6 md:p-7" data-testid={`public-security-section-${section.key}`}>
      <div className="flex items-start gap-4 mb-4">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${accent}12`, color: aText }}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-semibold text-[19px] text-[#0A0A0A]" data-testid={`public-security-title-${section.key}`}>{section.title}</h2>
          {section.overview && (
            <p className="text-[15px] text-[#334155] leading-relaxed mt-1.5">{section.overview}</p>
          )}
        </div>
      </div>

      {section.bullets && section.bullets.length > 0 && (
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 mb-4" data-testid={`public-security-bullets-${section.key}`}>
          {section.bullets.map((b, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-[#334155]">
              <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" style={{ color: aText }} /> <span>{b}</span>
            </li>
          ))}
        </ul>
      )}

      {(section.related_policies?.length > 0 || section.related_documents?.length > 0) && (
        <div className="rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-4 mb-3">
          <div className="text-[11px] uppercase tracking-wider text-[#64748B] font-semibold mb-2 flex items-center gap-1.5">
            <ClipboardCheck className="w-3.5 h-3.5" /> Supporting documentation
          </div>
          <ul className="divide-y divide-[#E2E8F0]">
            {(section.related_policies || []).map((p) => renderDocRow(p, 'policy'))}
            {(section.related_documents || []).map((d) => renderDocRow(d, 'doc'))}
          </ul>
        </div>
      )}

      {section.related_controls?.length > 0 && (
        <div className="rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-4 mb-3" data-testid={`public-security-controls-${section.key}`}>
          <div className="text-[11px] uppercase tracking-wider text-[#64748B] font-semibold mb-2 flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5" /> Related controls
          </div>
          <ul className="space-y-1.5">
            {section.related_controls.slice(0, 8).map((c) => (
              <li key={c.id} className="text-sm text-[#334155] flex items-start gap-2">
                <CheckCircle2 className="w-3.5 h-3.5 mt-1 shrink-0" style={{ color: aText }} />
                <span className="min-w-0">{c.title}{c.framework && <span className="text-xs text-[#94A3B8] ml-2">· {c.framework}</span>}</span>
              </li>
            ))}
            {section.related_controls.length > 8 && (
              <li className="text-xs">
                <button type="button" onClick={() => navigate('compliance')} className="font-semibold inline-flex items-center gap-1 hover:underline" style={{ color: aText }}>
                  + {section.related_controls.length - 8} more in Compliance <ArrowRight className="w-3 h-3" />
                </button>
              </li>
            )}
          </ul>
        </div>
      )}

      {section.related_faqs?.length > 0 && (
        <div className="rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-4" data-testid={`public-security-faqs-${section.key}`}>
          <div className="text-[11px] uppercase tracking-wider text-[#64748B] font-semibold mb-2 flex items-center gap-1.5">
            <HelpCircle className="w-3.5 h-3.5" /> Related FAQs
          </div>
          <ul className="space-y-2">
            {section.related_faqs.slice(0, 5).map((f) => (
              <li key={f.id}>
                <button type="button" onClick={() => navigate('faq')} className="text-left text-sm text-[#334155] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F62FE]/30 rounded">
                  {f.question}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Phase 1 audit-requirements: program cards + gap statements — live
          INSIDE the section they belong to as progressive-disclosure
          children. Tier gating is enforced by the backend sanitizer; this
          component only renders whatever survived the boundary. */}
      {section.program_card && (
        <ProgramCardBlock ctx={ctx} sectionKey={section.key} card={section.program_card} />
      )}
      {section.gap_statements && section.gap_statements.length > 0 && (
        <GapStatementList ctx={ctx} sectionKey={section.key} gaps={section.gap_statements} />
      )}
    </section>
  );
};

// ---------------------------------------------------------------------------
// Phase 1 audit-requirements: Program-card renderers.
// The backend sanitizer already stripped anything gated; every field here
// is safe for the emitted tier. UI simply shows "Detail available under
// Request/NDA" affordances where the sanitizer signalled `detail_available_via`.
// ---------------------------------------------------------------------------

const TierAffordance = ({ tier, onRequest, label }) => {
  if (!tier || tier === 'public') return null;
  const meta = tier === 'nda'
    ? { text: 'Available under NDA', cls: 'bg-[#F8FAFC] text-[#475569] border-[#CBD5E1]' }
    : { text: 'Available on request', cls: 'bg-[#EFF6FF] text-[#1D4ED8] border-[#BFDBFE]' };
  return (
    <button type="button" onClick={onRequest}
      className={`inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full border font-medium hover:opacity-80 transition-opacity ${meta.cls}`}
      data-testid={`public-affordance-${label}-${tier}`}>
      <Lock className="w-3 h-3" /> {meta.text}
    </button>
  );
};

const VaptProgramBlock = ({ ctx, sectionKey, card }) => {
  const { openRequest, aText } = ctx;
  const scope = card.scope || {};
  const sla = card.sla_matrix || {};
  const scopePills = ['web', 'mobile', 'api', 'infrastructure', 'ai'].filter((k) => scope[k]);
  return (
    <div className="mt-5 rounded-xl border border-[#E2E8F0] bg-[#FBFCFE] p-4" data-testid={`public-vapt-${sectionKey}`}>
      <div className="flex items-center justify-between gap-2 mb-3">
        <h4 className="font-semibold text-[13px] text-[#0A0A0A]">VAPT program</h4>
        <TierAffordance tier={card.tier} label="vapt" onRequest={() => openRequest(['VAPT full remediation register'])} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
        {card.last_completed_at && <div><div className="text-[#94A3B8] uppercase tracking-wider text-[10px]">Last completed</div><div className="font-medium text-[#0A0A0A]">{card.last_completed_at}</div></div>}
        {card.next_scheduled_at && <div><div className="text-[#94A3B8] uppercase tracking-wider text-[10px]">Next scheduled</div><div className="font-medium text-[#0A0A0A]">{card.next_scheduled_at}</div></div>}
        {card.assessor_name && <div><div className="text-[#94A3B8] uppercase tracking-wider text-[10px]">Assessor</div><div className="font-medium text-[#0A0A0A]">{card.assessor_name}{card.independent && ' · Independent'}</div></div>}
      </div>
      {scopePills.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-3" data-testid={`public-vapt-scope-${sectionKey}`}>
          {scopePills.map((s) => <span key={s} className="text-[10px] px-2 py-0.5 rounded-full bg-white border border-[#E2E8F0] text-[#334155] font-medium capitalize">{s === 'ai' ? 'AI' : s}</span>)}
        </div>
      )}
      {card.findings_summary && (
        <div className="mt-3 grid grid-cols-4 gap-2 text-center" data-testid={`public-vapt-findings-${sectionKey}`}>
          {['critical', 'high', 'medium', 'low'].map((sev) => (
            <div key={sev} className="rounded-lg bg-white border border-[#E2E8F0] py-2">
              <div className="text-[15px] font-semibold" style={{ color: aText }}>{card.findings_summary[`${sev}_open`] || 0}</div>
              <div className="text-[10px] uppercase tracking-wider text-[#94A3B8]">{sev} open</div>
            </div>
          ))}
        </div>
      )}
      {card.findings_aggregate && (
        <div className="mt-3 rounded-lg bg-white border border-[#E2E8F0] p-3 text-xs text-[#475569]" data-testid={`public-vapt-findings-aggregate-${sectionKey}`}>
          <span className="font-semibold text-[#0A0A0A]">{card.findings_aggregate.total_open} findings tracked</span>
          {card.findings_aggregate.as_of && <span className="text-[#94A3B8]"> · as of {card.findings_aggregate.as_of}</span>}
          <span className="text-[#94A3B8]"> — severity breakdown available on request</span>
        </div>
      )}
      {(sla.critical_days || sla.high_days) && (
        <div className="mt-3 text-[11px] text-[#475569] flex flex-wrap gap-x-3 gap-y-1">
          <span className="text-[#94A3B8]">SLAs:</span>
          {sla.critical_days && <span>Critical &lt; <b>{sla.critical_days}d</b></span>}
          {sla.high_days && <span>High &lt; <b>{sla.high_days}d</b></span>}
          {sla.medium_days && <span>Medium &lt; <b>{sla.medium_days}d</b></span>}
          {sla.low_days && <span>Low &lt; <b>{sla.low_days}d</b></span>}
        </div>
      )}
      {card.continuing_assurance && (
        <p className="mt-3 text-xs text-[#334155] leading-relaxed">{card.continuing_assurance}</p>
      )}
    </div>
  );
};

const DataMapBlock = ({ ctx, sectionKey, card }) => {
  const { openRequest } = ctx;
  const [showAll, setShowAll] = useState(false);
  const rows = card.rows || [];
  // Progressive disclosure: preview 5-8 rows initially, "View all" when public.
  const previewLimit = card.rows_public_preview || Math.min(rows.length, 6);
  const shown = (card.tier === 'public' && showAll) ? rows : rows.slice(0, previewLimit);
  const hasMore = card.tier === 'public' && rows.length > previewLimit && !showAll;
  const isPublic = card.tier === 'public';
  return (
    <div className="mt-5 rounded-xl border border-[#E2E8F0] bg-[#FBFCFE] p-4" data-testid={`public-data-map-${sectionKey}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <h4 className="font-semibold text-[13px] text-[#0A0A0A]">Data map</h4>
        <TierAffordance tier={card.tier} label="datamap" onRequest={() => openRequest(['Full data inventory'])} />
      </div>
      {card.summary && <p className="text-xs text-[#334155] mb-3 leading-relaxed">{card.summary}</p>}
      {shown.length > 0 && (
        <div className="overflow-x-auto -mx-1">
          <table className="w-full text-xs" data-testid={`public-data-map-table-${sectionKey}`}>
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-[#94A3B8]">
                <th className="py-1.5 px-2 font-semibold">Data</th>
                <th className="py-1.5 px-2 font-semibold">Purpose</th>
                {isPublic && <th className="py-1.5 px-2 font-semibold">Encryption</th>}
                {isPublic && <th className="py-1.5 px-2 font-semibold">Retention</th>}
                <th className="py-1.5 px-2 font-semibold">Region</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F1F5F9]">
              {shown.map((r) => (
                <tr key={r.id} className="align-top" data-testid={`public-data-map-row-${r.id}`}>
                  <td className="py-2 px-2 font-medium text-[#0A0A0A]">{r.data_category}</td>
                  <td className="py-2 px-2 text-[#475569]">{r.purpose || '—'}</td>
                  {isPublic && <td className="py-2 px-2 text-[#475569]">{r.encryption_at_rest || ''}{r.encryption_in_transit && r.encryption_at_rest ? ' · ' : ''}{r.encryption_in_transit || ''}</td>}
                  {isPublic && <td className="py-2 px-2 text-[#475569]">{r.retention_window || '—'}</td>}
                  <td className="py-2 px-2 text-[#475569]">{r.region || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {hasMore && (
        <button type="button" onClick={() => setShowAll(true)}
          className="mt-3 text-xs font-semibold text-[#0F62FE] hover:underline"
          data-testid={`public-data-map-viewall-${sectionKey}`}>
          View all {rows.length} rows →
        </button>
      )}
      {!isPublic && card.row_total > shown.length && (
        <p className="mt-2 text-xs text-[#94A3B8]" data-testid={`public-data-map-hidden-${sectionKey}`}>
          Showing {shown.length} of {card.row_total} categories · full inventory available {card.detail_available_via === 'nda' ? 'under NDA' : 'on request'}.
        </p>
      )}
    </div>
  );
};

const VendorAccessBlock = ({ ctx, sectionKey, card }) => {
  const { openRequest } = ctx;
  const yesNo = (v) => v ? 'Yes' : 'No';
  const items = [
    ['Customer approval required',      card.customer_approval_required],
    ['Just-in-time access',              card.jit_access],
    ['VPN required',                     card.vpn_required],
    ['Bastion / jump host',              card.bastion_required],
    ['Privileged activity logged',       card.privileged_activity_logged],
  ];
  return (
    <div className="mt-5 rounded-xl border border-[#E2E8F0] bg-[#FBFCFE] p-4" data-testid={`public-vendor-access-${sectionKey}`}>
      <div className="flex items-center justify-between gap-2 mb-3">
        <h4 className="font-semibold text-[13px] text-[#0A0A0A]">How our teams access customer data</h4>
        <TierAffordance tier={card.tier} label="vendor-access" onRequest={() => openRequest(['Access control matrix and evidence'])} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-1.5 gap-x-4 text-xs" data-testid={`public-vendor-access-controls-${sectionKey}`}>
        {items.map(([label, val]) => (
          <div key={label} className="flex items-center gap-2">
            <CheckCircle2 className={`w-3.5 h-3.5 ${val ? 'text-[#047857]' : 'text-[#CBD5E1]'}`} />
            <span className="text-[#475569] flex-1">{label}</span>
            <span className={`font-semibold ${val ? 'text-[#047857]' : 'text-[#94A3B8]'}`}>{yesNo(val)}</span>
          </div>
        ))}
      </div>
      {card.access_review_cadence && (
        <div className="mt-3 text-xs text-[#475569]">
          Access reviews: <b className="text-[#0A0A0A]">{card.access_review_cadence}</b>
          {card.last_access_review_at && <span> · last review <b>{card.last_access_review_at}</b></span>}
        </div>
      )}
      {card.teams_with_prod_access && card.teams_with_prod_access.length > 0 && (
        <div className="mt-2 text-xs">
          <span className="text-[#94A3B8]">Teams with production access: </span>
          {card.teams_with_prod_access.map((t) => (
            <span key={t} className="inline-block mr-1 px-1.5 py-0.5 rounded bg-white border border-[#E2E8F0] text-[#334155]">{t}</span>
          ))}
        </div>
      )}
    </div>
  );
};

const DrProgramBlock = ({ ctx, sectionKey, card }) => {
  const { openRequest, aText } = ctx;
  return (
    <div className="mt-5 rounded-xl border border-[#E2E8F0] bg-[#FBFCFE] p-4" data-testid={`public-dr-${sectionKey}`}>
      <div className="flex items-center justify-between gap-2 mb-3">
        <h4 className="font-semibold text-[13px] text-[#0A0A0A]">Business continuity & disaster recovery</h4>
        <TierAffordance tier={card.tier} label="dr" onRequest={() => openRequest(['DR drill report'])} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-lg bg-white border border-[#E2E8F0] py-2 text-center">
          <div className="text-[15px] font-semibold" style={{ color: aText }}>{card.rto_hours}h</div>
          <div className="text-[10px] uppercase tracking-wider text-[#94A3B8]">RTO</div>
        </div>
        <div className="rounded-lg bg-white border border-[#E2E8F0] py-2 text-center">
          <div className="text-[15px] font-semibold" style={{ color: aText }}>{card.rpo_minutes}m</div>
          <div className="text-[10px] uppercase tracking-wider text-[#94A3B8]">RPO</div>
        </div>
      </div>
      {card.backup_cadence && <p className="mt-3 text-xs text-[#334155]">Backups: {card.backup_cadence}</p>}
      {card.last_drill_at && <p className="mt-1 text-xs text-[#334155]">Last DR drill: <b>{card.last_drill_at}</b>{card.last_drill_outcome && ` — ${card.last_drill_outcome}`}</p>}
      {card.ir_integration_note && <p className="mt-1 text-xs text-[#475569] italic">{card.ir_integration_note}</p>}
    </div>
  );
};

const ProgramCardBlock = ({ ctx, sectionKey, card }) => {
  if (!card || !card.kind) return null;
  switch (card.kind) {
    case 'vapt_program':    return <VaptProgramBlock ctx={ctx} sectionKey={sectionKey} card={card} />;
    case 'data_map':        return <DataMapBlock ctx={ctx} sectionKey={sectionKey} card={card} />;
    case 'vendor_access':   return <VendorAccessBlock ctx={ctx} sectionKey={sectionKey} card={card} />;
    case 'dr_program':      return <DrProgramBlock ctx={ctx} sectionKey={sectionKey} card={card} />;
    default: return null;
  }
};

const GAP_STATUS_META = {
  planned:       { label: 'Planned',       cls: 'bg-[#F1F5F9] text-[#475569] border-[#CBD5E1]' },
  in_progress:   { label: 'In progress',   cls: 'bg-[#EFF6FF] text-[#1D4ED8] border-[#BFDBFE]' },
  accepted_risk: { label: 'Accepted risk', cls: 'bg-[#FEF3C7] text-[#92400E] border-[#FCD34D]' },
  closed:        { label: 'Closed',        cls: 'bg-[#ECFDF5] text-[#047857] border-[#A7F3D0]' },
};

const GapStatementList = ({ ctx, sectionKey, gaps }) => {
  const { openRequest } = ctx;
  return (
    <div className="mt-4 space-y-2" data-testid={`public-gaps-${sectionKey}`}>
      <p className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-semibold">In-flight remediation</p>
      {gaps.map((g) => {
        const meta = GAP_STATUS_META[g.status] || GAP_STATUS_META.planned;
        return (
          <div key={g.id} className="rounded-lg border border-dashed border-[#E2E8F0] bg-white p-3" data-testid={`public-gap-${g.id}`}>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="font-medium text-[13px] text-[#0A0A0A] flex-1 min-w-0">{g.title}</div>
              <span className={`text-[10px] px-2 py-0.5 rounded-full border ${meta.cls}`}>{meta.label}</span>
              <TierAffordance tier={g.visibility === 'public' ? 'public' : g.visibility} label={`gap-${g.id}`} onRequest={() => openRequest([g.title])} />
            </div>
            {g.current_state && <p className="text-xs text-[#475569] mt-1">{g.current_state}</p>}
            {g.compensating_controls && <p className="text-xs text-[#334155] mt-1"><span className="text-[#94A3B8]">Compensating: </span>{g.compensating_controls}</p>}
            {(g.owner || g.target_date) && (
              <p className="text-[11px] text-[#94A3B8] mt-1">
                {g.owner && <>Owner: <b className="text-[#475569]">{g.owner}</b></>}
                {g.owner && g.target_date && ' · '}
                {g.target_date && <>Target: <b className="text-[#475569]">{g.target_date}</b></>}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
};

const SecurityView = ({ ctx }) => {
  const { data, accent, aText, onAcc, openRequest, navigate } = ctx;
  const profile = data.security_profile || {};
  const sections = SECURITY_SECTION_ORDER
    .map((k) => profile[k])
    .filter(Boolean);
  // Private by Default / strict manual control: sections that the admin has NOT
  // published are never advertised on the public Security page (no titles, no
  // "available on request" strip). A section appears only when explicitly
  // published, so a Private or unconfigured section is never disclosed here.

  return (
    <div data-testid="public-security">
      <PageHead accent={accent} eyebrow="Security" title="Security posture"
        subtitle="How we protect customer data across identity, infrastructure, applications, monitoring, and operations." />

      {sections.length === 0 ? (
        <EmptyState icon={ShieldCheck} title="Security details available on request"
          hint="Detailed security posture is shared privately during vendor assessments. Use the button below to request our security package."
          action={
            <button type="button" onClick={() => openRequest(['Complete security package'])}
              className="px-5 py-2.5 rounded-full text-[11px] uppercase tracking-[0.14em] font-semibold hover:opacity-90 transition-opacity inline-flex items-center gap-1.5"
              style={{ backgroundColor: accent, color: onAcc }} data-testid="public-security-request-package-empty">
              <Send className="w-3.5 h-3.5" /> Request security package
            </button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Left rail: anchored section navigation (sticky on desktop) */}
          <aside className="hidden lg:block lg:col-span-3" data-testid="public-security-anchor-nav">
            <nav className="sticky top-32 space-y-1">
              <p className="text-[11px] uppercase tracking-wider text-[#94A3B8] font-semibold mb-3">On this page</p>
              {sections.map((s) => (
                <a key={s.key} href={`#security-${s.key.replace(/_/g, '-')}`}
                  className="block px-3 py-2 rounded-lg text-sm font-medium text-[#475569] hover:text-[#0A0A0A] hover:bg-[#F1F5F9] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F62FE]/30"
                  data-testid={`public-security-anchor-${s.key}`}>
                  {s.title}
                </a>
              ))}
            </nav>
          </aside>

          <div className="lg:col-span-9 space-y-6" data-testid="public-security-sections">
            {sections.map((s) => <SecuritySectionCard key={s.key} ctx={ctx} section={s} />)}

            <div className="mt-4 rounded-2xl border border-[#E2E8F0] bg-[#FBFCFE] p-6 flex items-start justify-between gap-6 flex-wrap" data-testid="public-security-cta">
              <div className="min-w-0">
                <h3 className="font-semibold text-[15px] text-[#0A0A0A]">Reviewing our security for a vendor assessment?</h3>
                <p className="text-sm text-[#475569] mt-1 max-w-lg">Request our complete security package — including SOC 2, penetration test summary, and detailed architecture.</p>
              </div>
              <div className="flex gap-2 shrink-0">
                <button type="button" onClick={() => openRequest(['Complete security package'])}
                  className="px-5 py-2.5 rounded-full text-[11px] uppercase tracking-[0.14em] font-semibold hover:opacity-90 transition-opacity inline-flex items-center gap-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#0F62FE]/50"
                  style={{ backgroundColor: accent, color: onAcc }} data-testid="public-security-request-package">
                  <Send className="w-3.5 h-3.5" /> Request package
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <CrossLinks ctx={ctx} current="security" targets={['compliance', 'resources', 'subprocessors', 'faq', 'contact']} />
    </div>
  );
};


const ComplianceView = ({ ctx }) => {
  const { data, accent, aText, frameworkParam, setFrameworkParam } = ctx;
  const frameworks = data.compliance_snapshot || [];
  const docs = data.documents || [];
  const controls = data.controls || [];
  const faqs = data.faqs || [];
  const showControls = (data.controls_settings || {}).show_controls === true && controls.length > 0;

  const activeToken = (frameworkParam || '').toLowerCase();
  const tokens = frameworks.map((f) => frameworkToken(f.name));
  const openToken = tokens.includes(activeToken) ? activeToken : '';
  const openFw = frameworks.find((f) => frameworkToken(f.name) === openToken);
  const toggleFw = (name) => { const tk = frameworkToken(name); setFrameworkParam(openToken === tk ? '' : tk); };

  const docFwTokens = (d) => {
    const list = (d.related_frameworks && d.related_frameworks.length) ? d.related_frameworks : (d.related_framework ? [d.related_framework] : []);
    return list.map(frameworkToken);
  };

  return (
    <div data-testid="public-compliance">
      <PageHead accent={accent} eyebrow="Compliance" title="Compliance &amp; certifications"
        subtitle="The frameworks we align to — and the evidence, policies, controls and answers behind each one." />

      {frameworks.length === 0 ? (
        <EmptyState icon={BadgeCheck} title="No compliance information published" hint="This company has not published compliance framework information yet." />
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="public-compliance-cards">
            {frameworks.map((fw) => {
              const tk = frameworkToken(fw.name);
              const isOpen = tk === openToken;
              return (
                <button key={fw.name} type="button" onClick={() => toggleFw(fw.name)} aria-expanded={isOpen}
                  data-testid={`public-compliance-fw-${tk}`}
                  className={`rounded-xl border bg-white p-4 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F62FE]/30 ${isOpen ? 'shadow-sm' : 'border-[#E2E8F0] hover:border-[#CBD5E1] hover:bg-[#F8FAFC]'}`}
                  style={isOpen ? { borderColor: accent } : undefined}>
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <span className="inline-flex items-center gap-2 min-w-0">
                      <BadgeCheck className="w-5 h-5 shrink-0" style={{ color: aText }} />
                      <span className="font-semibold text-sm text-[#0A0A0A] truncate">{fw.name}</span>
                    </span>
                    <ChevronDown className={`w-4 h-4 shrink-0 text-[#94A3B8] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                  </div>
                  <StatusBadge status={fw.status || 'In Progress'} />
                  {fw.last_updated && <div className="text-[11px] text-[#94A3B8] mt-2">Last reviewed {fmtDate(fw.last_updated)}</div>}
                </button>
              );
            })}
          </div>

          {!openToken && (
            <p className="text-sm text-[#64748B] mt-5" data-testid="public-compliance-hint">
              Select a framework to see its evidence, policies, controls and related FAQs.
            </p>
          )}

          {openToken && openFw && (
            <FrameworkPanel ctx={ctx} fw={openFw} token={openToken}
              evidence={docs.filter((d) => !d.is_policy && d.category !== 'Policy' && docFwTokens(d).includes(openToken))}
              policies={docs.filter((d) => (d.is_policy || d.category === 'Policy') && docFwTokens(d).includes(openToken))}
              controls={showControls ? controls.filter((c) => frameworkToken(c.framework) === openToken) : []}
              faqs={faqs.filter((f) => fwKeywords(openFw.name).some((k) => `${f.question} ${f.answer}`.toLowerCase().includes(k)))} />
          )}
        </>
      )}

      <CrossLinks ctx={ctx} current="compliance" targets={['resources', 'subprocessors', 'faq', 'contact']} />
    </div>
  );
};

/* ============================ Subprocessors ============================ */
const SubprocessorsView = ({ ctx }) => {
  const { data, accent, aText } = ctx;
  const [filter, setFilter] = useState('All');
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(null);
  const all = data.subprocessors || [];
  const filters = ['All', 'Active', 'Planned', 'Removed'];
  const rows = all.filter((s) => (filter === 'All' || (s.status || '').toLowerCase().includes(filter.toLowerCase())) && `${s.vendor_name} ${s.purpose} ${s.data_processed}`.toLowerCase().includes(q.toLowerCase()));

  const activeCount = all.filter((s) => /active/i.test(s.status || '')).length;
  const regions = new Set(all.map((s) => s.location).filter(Boolean)).size;

  return (
    <div data-testid="public-subprocessors">
      <PageHead accent={accent} eyebrow="Subprocessors" title="Subprocessor registry" subtitle="Third-party vendors that support our service delivery, the data they process and where." right={<SearchBox q={q} setQ={setQ} label="Search vendors" testid="public-sub-search" />} />

      <SummaryLine testid="public-subprocessors-summary" items={[
        { value: all.length, label: all.length === 1 ? 'subprocessor' : 'subprocessors' },
        activeCount > 0 && { value: activeCount, label: 'active' },
        regions > 0 && { value: regions, label: regions === 1 ? 'region' : 'regions' },
      ]} />

      <div className="flex flex-wrap gap-2 mb-4">{filters.map((f) => <Chip key={f} active={filter === f} onClick={() => setFilter(f)} accent={accent} testid={`public-sub-filter-${f.toLowerCase()}`}>{f}</Chip>)}</div>

      {all.length === 0 ? (
        <EmptyState icon={Server} title="No subprocessors disclosed" hint="This company has not listed any subprocessors yet." />
      ) : rows.length === 0 ? (
        <EmptyState icon={Server} title="No vendors match" hint="Try a different filter or clear your search." />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-[#E2E8F0] bg-white">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-[#F8FAFC] text-[#64748B] text-xs uppercase tracking-wider"><tr>
              <th scope="col" className="text-left px-4 py-3 font-semibold">Vendor</th>
              <th scope="col" className="text-left px-4 py-3 font-semibold">Purpose</th>
              <th scope="col" className="text-left px-4 py-3 font-semibold hidden md:table-cell">Data processed</th>
              <th scope="col" className="text-left px-4 py-3 font-semibold hidden lg:table-cell">Region</th>
              <th scope="col" className="text-left px-4 py-3 font-semibold">Status</th>
              <th scope="col" className="text-right px-4 py-3 font-semibold">Privacy</th>
            </tr></thead>
            <tbody>
              {rows.map((s, i) => {
                const isOpen = open === i;
                return (
                  <React.Fragment key={s.id}>
                    <tr className="border-t border-[#E2E8F0]" data-testid={`public-subprocessor-${i}`}>
                      <td className="px-4 py-3">
                        <button onClick={() => setOpen(isOpen ? null : i)} aria-expanded={isOpen} data-testid={`public-sub-expand-${i}`}
                          className="inline-flex items-center gap-2 font-medium text-[#0A0A0A] hover:text-[#0F62FE] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F62FE]/30 rounded">
                          <ChevronRight className={`w-4 h-4 text-[#94A3B8] transition-transform ${isOpen ? 'rotate-90' : ''}`} />{s.vendor_name}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-[#475569]">{s.purpose || '—'}</td>
                      <td className="px-4 py-3 text-[#475569] hidden md:table-cell">{s.data_processed || '—'}</td>
                      <td className="px-4 py-3 text-[#475569] hidden lg:table-cell">{s.location || '—'}</td>
                      <td className="px-4 py-3"><StatusBadge status={s.status} /></td>
                      <td className="px-4 py-3 text-right">{s.privacy_url ? <a href={s.privacy_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F62FE]/30 rounded" style={{ color: aText }}>Policy <ExternalLink className="w-3 h-3" /></a> : '—'}</td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-[#FBFCFE] md:hidden" data-testid={`public-sub-detail-${i}`}>
                        <td colSpan={6} className="px-4 py-4">
                          <div className="space-y-1.5 text-xs text-[#64748B]">
                            <div><span className="text-[#94A3B8]">Data processed:</span> <span className="text-[#334155]">{s.data_processed || '—'}</span></div>
                            <div><span className="text-[#94A3B8]">Region:</span> <span className="text-[#334155]">{s.location || '—'}</span></div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <CrossLinks ctx={ctx} current="subprocessors" targets={['resources', 'compliance', 'contact']} />
    </div>
  );
};

/* ============================ FAQ ============================ */
const FAQ_TOPICS = ['All', 'Security', 'Privacy', 'Compliance', 'AI', 'Data'];
const faqMatchTopic = (f, topic) => {
  if (topic === 'All') return true;
  const t = `${f.question} ${f.answer}`.toLowerCase();
  const map = { Security: ['secur', 'encrypt', 'access', 'incident'], Privacy: ['privac', 'personal', 'gdpr'], Compliance: ['complian', 'soc', 'iso', 'audit', 'certif', 'framework'], AI: [' ai', 'ai ', 'model', 'machine learning'], Data: ['data', 'retention', 'subprocessor', 'storage'] };
  return (map[topic] || []).some((k) => t.includes(k));
};
const FAQView = ({ ctx }) => {
  const { data, accent, aText, slug, mode } = ctx;
  const [open, setOpen] = useState(-1);
  const [q, setQ] = useState('');
  const [topic, setTopic] = useState('All');
  const all = data.faqs || [];
  const rows = all.filter((f) => faqMatchTopic(f, topic) && `${f.question} ${f.answer}`.toLowerCase().includes(q.toLowerCase()));
  useSearchTracking(mode, slug, q, 'faq');

  return (
    <div data-testid="public-faq">
      <PageHead accent={accent} eyebrow="FAQ" title="Frequently asked questions" subtitle="Quick answers to common security, privacy and compliance questions." right={<SearchBox q={q} setQ={setQ} label="Search questions" testid="public-faq-search" />} />

      <SummaryLine testid="public-faq-summary" items={[
        { value: all.length, label: all.length === 1 ? 'question' : 'questions' },
        { value: rows.length, label: 'showing' },
      ]} />

      <div className="flex flex-wrap gap-2 mb-5">{FAQ_TOPICS.map((t) => <Chip key={t} active={topic === t} onClick={() => setTopic(t)} accent={accent} testid={`public-faq-topic-${t.toLowerCase()}`}>{t}</Chip>)}</div>

      {rows.length === 0 ? (
        <EmptyState icon={HelpCircle} title="No questions match" hint="Try a different topic or search term — or contact our security team directly." />
      ) : (
        <div className="space-y-3 max-w-3xl">
          {rows.map((f, i) => {
            const isOpen = open === i;
            return (
              <div key={f.id} className="rounded-xl border border-[#E2E8F0] bg-white overflow-hidden">
                <button type="button" onClick={() => setOpen(isOpen ? -1 : i)} aria-expanded={isOpen} data-testid={`public-faq-${i}`} className="w-full flex items-center justify-between gap-4 text-left px-5 py-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F62FE]/30">
                  <span className="font-semibold text-sm">{f.question}</span><ChevronDown className={`w-5 h-5 shrink-0 text-[#94A3B8] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </button>
                <div className={`grid transition-all duration-300 ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}><div className="overflow-hidden"><p className="text-sm text-[#475569] leading-relaxed px-5 pb-5">{f.answer}</p></div></div>
              </div>
            );
          })}
        </div>
      )}

      <CrossLinks ctx={ctx} current="faq" targets={['resources', 'compliance', 'contact']} />
    </div>
  );
};

/* ============================ Contact ============================ */
const ContactView = ({ ctx }) => {
  const { data, accent, aText, onAcc, openRequest } = ctx;
  const email = data.security_contact_email;
  const disc = data.disclosure || {};
  const securityTxtUrl = safeUrl(disc.security_txt_url || data.security_txt_url);
  const pgpUrl = safeUrl(disc.pgp_key_url || data.pgp_key_url);
  const vulnReportEmail = disc.report_email || email;

  return (
    <div data-testid="public-contact">
      <PageHead
        accent={accent}
        eyebrow="Contact"
        title="Contact our security team"
        subtitle="Request documentation, report a vulnerability, or reach us directly. This page is intended for security reviewers, procurement, and researchers."
      />

      <SummaryLine testid="public-contact-summary" items={[
        { value: email ? 'Available' : '—', label: 'security contact' },
        disc.response_sla && { value: disc.response_sla, label: 'response SLA' },
        { value: (disc.statement || '').trim() ? 'Published' : '—', label: 'disclosure policy' },
      ]} />

      {/* Primary action — Request Documentation is the enterprise-first
          entry point. Email becomes a secondary CTA. */}
      <div className="rounded-2xl border border-[#E2E8F0] bg-white p-8 mb-6" data-testid="public-contact-primary">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${accent}14` }}>
            <Send className="w-6 h-6" style={{ color: aText }} />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold text-xl mb-1">{CTA.requestDocs}</h3>
            <p className="text-sm text-[#64748B] mb-4 max-w-2xl">Get security overviews, DPA, questionnaires, certificates and other collateral. We track requests and respond within our published SLA.</p>
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => openRequest()}
                data-testid="public-contact-primary-cta"
                className="px-5 py-2.5 rounded-full text-xs uppercase tracking-[0.16em] font-semibold hover:opacity-90 transition-opacity inline-flex items-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#0F62FE]/50"
                style={{ backgroundColor: accent, color: onAcc }}
              >
                <Send className="w-3.5 h-3.5" /> {CTA.requestDocs}
              </button>
              {email && (
                <a
                  href={`mailto:${email}`}
                  data-testid="public-contact-email-secondary"
                  className="px-5 py-2.5 rounded-full text-xs uppercase tracking-[0.16em] font-semibold border border-[#CBD5E1] hover:bg-[#F8FAFC] transition-colors inline-flex items-center gap-2"
                >
                  <Mail className="w-3.5 h-3.5" /> Email security
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Enterprise-grade secondary cards. Only rendered when we have
          meaningful content — never fabricates data. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {email && (
          <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6" data-testid="public-contact-security-block">
            <div className="flex items-center gap-3 mb-2">
              <Mail className="w-5 h-5" style={{ color: aText }} />
              <h3 className="font-semibold text-base">Security contact</h3>
            </div>
            <p className="text-sm text-[#475569] leading-relaxed mb-3">Direct-line security email for reviewers and vendor risk teams.</p>
            <div className="flex flex-wrap items-center gap-2">
              <a href={`mailto:${email}`} data-testid="public-contact-email" className="text-sm font-medium hover:underline min-w-0 break-words [overflow-wrap:anywhere]" style={{ color: aText }}>{email}</a>
              <button onClick={() => copyText(email)} data-testid="public-contact-copy" aria-label="Copy security email" className="p-1.5 rounded-full border border-[#E2E8F0] text-[#64748B] hover:text-[#0A0A0A] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F62FE]/30 shrink-0" title="Copy email"><Copy className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        )}

        <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6" data-testid="public-contact-vuln-block">
          <div className="flex items-center gap-3 mb-2">
            <ShieldAlert className="w-5 h-5" style={{ color: aText }} />
            <h3 className="font-semibold text-base">Vulnerability reporting</h3>
          </div>
          <p className="text-sm text-[#475569] leading-relaxed mb-3">Security researchers are welcomed. Please share reproduction details, affected components, and impact. Do not access data that isn&apos;t yours.</p>
          {vulnReportEmail && (
            <a href={`mailto:${vulnReportEmail}`} data-testid="public-contact-vuln-email" className="text-sm font-medium hover:underline min-w-0 break-words [overflow-wrap:anywhere]" style={{ color: aText }}>{vulnReportEmail}</a>
          )}
          {disc.reporting_instructions && (
            <p className="text-xs text-[#64748B] mt-3 leading-relaxed">{disc.reporting_instructions}</p>
          )}
        </div>

        {(disc.statement || '').trim() && (
          <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6" data-testid="public-disclosure">
            <div className="flex items-center gap-3 mb-2">
              <ShieldCheck className="w-5 h-5" style={{ color: aText }} />
              <h3 className="font-semibold text-base">Responsible disclosure policy</h3>
            </div>
            <p className="text-sm text-[#475569] leading-relaxed">{disc.statement}</p>
          </div>
        )}

        {disc.response_sla && (
          <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6" data-testid="public-contact-sla-block">
            <div className="flex items-center gap-3 mb-2">
              <Timer className="w-5 h-5" style={{ color: aText }} />
              <h3 className="font-semibold text-base">Response SLA</h3>
            </div>
            <p className="text-sm text-[#475569] leading-relaxed">{disc.response_sla}</p>
          </div>
        )}

        {(securityTxtUrl || pgpUrl) && (
          <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6 md:col-span-2" data-testid="public-contact-security-txt-block">
            <div className="flex items-center gap-3 mb-2">
              <ShieldQuestion className="w-5 h-5" style={{ color: aText }} />
              <h3 className="font-semibold text-base">For researchers</h3>
            </div>
            <p className="text-sm text-[#475569] leading-relaxed mb-3">Machine-readable security metadata and encryption keys.</p>
            <div className="flex flex-wrap gap-3">
              {securityTxtUrl && (
                <a href={securityTxtUrl} target="_blank" rel="noreferrer" data-testid="public-contact-security-txt" className="inline-flex items-center gap-1.5 text-sm font-semibold hover:underline" style={{ color: aText }}>
                  security.txt <ExternalLink className="w-3 h-3" />
                </a>
              )}
              {pgpUrl && (
                <a href={pgpUrl} target="_blank" rel="noreferrer" data-testid="public-contact-pgp" className="inline-flex items-center gap-1.5 text-sm font-semibold hover:underline" style={{ color: aText }}>
                  PGP key <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          </div>
        )}
      </div>

      <CrossLinks ctx={ctx} current="contact" targets={['resources', 'compliance', 'faq']} />
    </div>
  );
};

/* ============================ Request Center ============================ */
// The single consolidated request experience — every "Request" entry point
// across the Trust Center opens this. Supports pre-selected documents,
// adding more gated/NDA documents, free-text asks, and an NDA notice.
const RequestCenter = ({ reqOpen, setReqOpen, submitted, submitting, submitRequest, form, setForm, data, accent, onAcc }) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQ, setPickerQ] = useState('');
  useEffect(() => { if (!reqOpen) { setPickerOpen(false); setPickerQ(''); } }, [reqOpen]);

  const items = form.requested_items || [];
  const selectedIds = new Set(items.filter((i) => i.id).map((i) => i.id));
  const requestable = (data.documents || []).filter((d) => d.access !== 'public');
  const pickerRows = requestable.filter((d) => `${d.title} ${d.doc_type} ${d.category}`.toLowerCase().includes(pickerQ.toLowerCase()));
  const hasNda = items.some((i) => i.access === 'nda');
  const removeItem = (idx) => setForm((p) => ({ ...p, requested_items: (p.requested_items || []).filter((_, i) => i !== idx) }));
  const toggleDoc = (d) => setForm((p) => {
    const cur = p.requested_items || [];
    if (cur.some((i) => i.id === d.id)) return { ...p, requested_items: cur.filter((i) => i.id !== d.id) };
    return { ...p, requested_items: [...cur, { id: d.id, title: d.title, access: d.access }] };
  });

  return (
    <Dialog open={reqOpen} onOpenChange={setReqOpen}>
      <DialogContent className="max-w-xl p-0 overflow-hidden bg-white max-h-[90vh] overflow-y-auto" data-testid="public-request-modal">
        <DialogTitle className="sr-only">Request Center</DialogTitle>
        <DialogDescription className="sr-only">Request access to security and compliance documentation.</DialogDescription>
        {submitted ? (
          <div className="p-10 text-center" data-testid="public-request-success">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-5" style={{ backgroundColor: `${accent}14` }}><CheckCircle2 className="w-7 h-7" style={{ color: accent }} /></div>
            <h3 className="font-semibold text-2xl mb-3">Request received</h3>
            <p className="text-sm text-[#64748B] leading-relaxed max-w-sm mx-auto mb-7">Thank you. The {data.company_name} security team will review your request and follow up at the email you provided.</p>
            <button onClick={() => setReqOpen(false)} data-testid="public-request-done" className="px-7 py-3 rounded-full text-xs uppercase tracking-[0.2em] font-semibold hover:opacity-90 transition-opacity" style={{ backgroundColor: accent, color: onAcc }}>Done</button>
          </div>
        ) : (
          <form onSubmit={submitRequest} className="p-8" data-testid="public-request-form">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] mb-2" style={{ color: accent }}>Request Center</p>
            <h3 className="font-semibold text-2xl mb-1">{hasNda ? 'Request under NDA' : 'Request Access'}</h3>
            <p className="text-sm text-[#64748B] leading-relaxed mb-5">One request for everything you need — the team reviews it and follows up.</p>

            {items.length > 0 && (
              <div className="mb-3 flex flex-wrap gap-1.5" data-testid="public-request-selected">
                {items.map((t, idx) => (
                  <span key={`${t.id || t.title}-${idx}`} className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-full" style={{ backgroundColor: `${accent}14`, color: accent }}>
                    <span className="max-w-[220px] truncate">{t.title}</span>
                    {t.access === 'nda' && <span className="text-[9px] font-bold uppercase tracking-wide border border-current rounded px-1">NDA</span>}
                    <button type="button" onClick={() => removeItem(idx)} aria-label={`Remove ${t.title}`} className="hover:opacity-70 focus:outline-none" data-testid={`public-request-remove-${idx}`}><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
            )}

            {requestable.length > 0 && (
              <div className="mb-4">
                <button type="button" onClick={() => setPickerOpen((v) => !v)} data-testid="public-request-add-docs"
                  className="text-xs font-semibold inline-flex items-center gap-1.5 hover:underline focus:outline-none" style={{ color: accent }}>
                  <Layers className="w-3.5 h-3.5" /> {pickerOpen ? 'Hide document list' : (items.length ? 'Add more documents' : 'Select documents')}
                </button>
                {pickerOpen && (
                  <div className="mt-2 rounded-lg border border-[#E2E8F0] overflow-hidden" data-testid="public-request-picker">
                    <input value={pickerQ} onChange={(e) => setPickerQ(e.target.value)} placeholder="Filter documents…" data-testid="public-request-picker-search"
                      className="w-full px-3 py-2 text-sm border-b border-[#E2E8F0] focus:outline-none" />
                    <div className="max-h-44 overflow-y-auto divide-y divide-[#F1F5F9]">
                      {pickerRows.map((d) => (
                        <label key={d.id} className="flex items-center gap-2.5 px-3 py-2 text-sm cursor-pointer hover:bg-[#F8FAFC]" data-testid={`public-request-pick-${d.id}`}>
                          <input type="checkbox" checked={selectedIds.has(d.id)} onChange={() => toggleDoc(d)} className="rounded shrink-0" />
                          <span className="min-w-0 flex-1 truncate text-[#0A0A0A]">{d.title}</span>
                          <AccessBadge access={d.access} />
                        </label>
                      ))}
                      {pickerRows.length === 0 && <div className="px-3 py-3 text-xs text-[#94A3B8]">No documents match.</div>}
                    </div>
                  </div>
                )}
              </div>
            )}

            {hasNda && (
              <div className="mb-4 rounded-lg border border-[#FDE68A] bg-[#FFFBEB] px-3.5 py-2.5 text-xs text-[#92400E] flex items-start gap-2" data-testid="public-request-nda-note">
                <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" />
                <span>Items marked <b>NDA</b> require a signed non-disclosure agreement before they can be shared. The team will coordinate this with you.</span>
              </div>
            )}

            <div className="space-y-4">
              {[
                { k: 'name', label: 'Full Name', type: 'text', required: true, ph: 'Jane Doe' },
                { k: 'company', label: 'Company', type: 'text', required: false, ph: 'Acme Inc.' },
                { k: 'email', label: 'Work Email', type: 'email', required: true, ph: 'jane@acme.com' },
                { k: 'job_title', label: 'Job Title', type: 'text', required: false, ph: 'Security Engineer' },
              ].map((f) => (
                <div key={f.k}>
                  <label className="block text-[11px] uppercase tracking-[0.15em] text-[#64748B] mb-1.5 font-semibold">{f.label}{f.required && <span style={{ color: accent }}> *</span>}</label>
                  <input type={f.type} required={f.required} placeholder={f.ph} value={form[f.k]} onChange={(e) => setForm((p) => ({ ...p, [f.k]: e.target.value }))} data-testid={`public-request-${f.k}`}
                    className="w-full rounded-lg border border-[#CBD5E1] bg-white px-4 py-2.5 text-sm focus:outline-none focus:border-[#0F62FE] focus:ring-2 focus:ring-[#0F62FE]/15 transition-all" />
                </div>
              ))}
              <div>
                <label className="block text-[11px] uppercase tracking-[0.15em] text-[#64748B] mb-1.5 font-semibold">Anything else you need? (optional)</label>
                <textarea rows={3} value={form.message} onChange={(e) => setForm((p) => ({ ...p, message: e.target.value }))} data-testid="public-request-message"
                  placeholder="e.g. completed questionnaires, architecture details, specific evidence…"
                  className="w-full rounded-lg border border-[#CBD5E1] bg-white px-4 py-2.5 text-sm focus:outline-none focus:border-[#0F62FE] focus:ring-2 focus:ring-[#0F62FE]/15 transition-all" />
              </div>
            </div>
            <button type="submit" disabled={submitting} data-testid="public-request-submit"
              className="mt-6 w-full px-8 py-3.5 rounded-full text-xs uppercase tracking-[0.2em] font-semibold hover:opacity-90 transition-opacity disabled:opacity-60 inline-flex items-center justify-center gap-2" style={{ backgroundColor: accent, color: onAcc }}>
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}{submitting ? 'Submitting…' : 'Submit Request'}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PublicTrustCenter;

